import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { isPieceUnit, normalizeUnit } from '../../utils/units';
import SearchableSelect from '../common/SearchableSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faArrowRight, faArrowLeft, faPlus, faTrash, faWarehouse, faEye, faPaperclip, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { validateNewCustomerData, validateExistingCustomerOrder, validateNewCustomerOrder, validateOrderItems } from '../../utils/validate';
import customersService from '../../services/customersService';
import ordersService from '../../services/ordersService';
import locationsService from '../../services/locationsService';
import productSizesService from '../../services/productSizesServices';
import warehouseService from '../../services/warehouseService';

import AddLocationModal from '../locations/AddLocationModal';
import CustomerWarehouseForm from '../customers/CustomerWarehouseForm';
import CoilPickStrip from '../coils/CoilPickStrip';
import { useCoilSettings } from '../../context/CoilSettingsContext';
import { formatCurrency } from '../../utils/format';
import AttachmentsPanel from '../common/AttachmentsPanel';
import { useTranslation } from 'react-i18next';
import {
  buildDeliveryLocationOptions,
  buildCustomerAddressDraft,
  isSyntheticLocationValue,
} from '../../utils/deliveryLocationOptions';
import useCustomerDeliveryLocation from '../../hooks/useCustomerDeliveryLocation';

const PAYMENT_TERMS_OPTIONS = [
    '100% Advance',
    'Against Invoice',
    'Against Delivery',
    '7 Days',
    '10 Days',
    '15 Days',
    '25 Days',
    '30 Days',
    '45 Days',
    '60 Days',
    'Against LC/Bill Discounting',
    'Others',
].map(v => ({ value: v, label: v }));

const sanitizeNumber = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const s = String(v).replace(/[^0-9.]/g, '');
  return s;
};

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// defaultNewCustomer created with useMemo inside component to capture localized placeholders

const emptyItem = () => ({ product_size_id: '', unit: 'kg', length: '6', length_note: '', quantity: '', quantity_in_pieces: '', cut_pieces: '', price: '', remark: '', load_by_pieces: false, coil_picks: [] });

// A saved line's coil allocations, flattened into the shape the picker works in. The
// allocations come back denormalised (batch, colour, kg/m), so reopening an order can
// render its picks without a second trip to the coil endpoint.
// The remaining/available fallbacks matter: without them a reopened pick would measure
// itself against 0 kg available and read as invalid the moment the picker is opened.
const coilPicksFromItem = (item) => (item?.coil_allocations || []).map(a => {
  const takeKg = a.take_kg ?? a.allocated_weight_kg ?? 0;
  return {
    coil_id: a.coil_id ?? a.id,
    take_kg: takeKg,
    batch_number: a.batch_number || '',
    colour: a.colour || '',
    is_loose_coil: a.is_loose_coil ?? false,
    kg_per_m: a.kg_per_m,
    remaining_weight_kg: a.remaining_weight_kg ?? takeKg,
    available_weight_kg: a.available_weight_kg ?? a.remaining_weight_kg ?? takeKg,
  };
});

// Unit vocabulary: staff uses 'm', portal/BE uses 'meters'; treat both as
// length-based. 'kg' is weight-based; 'pieces'/'pcs' are already piece counts.
const isLengthUnit = (u) => u === 'm' || u === 'meters';
const isWeightUnit = (u) => u === 'kg';

// The quantity a line's amount is priced against. A pieces line is priced per
// piece, so its amount uses the piece count (quantity_in_pieces); its `quantity`
// holds the optional kg weight. kg/m are priced against quantity. Mirrors the
// backend amount_basis (fall back to quantity if a piece count isn't present).
const amountBasisQty = (it) => {
  if (isPieceUnit(it?.unit)) {
    const pcs = parseInt(it?.quantity_in_pieces, 10);
    if (pcs > 0) return pcs;
    return parseFloat(it?.quantity) || 0;
  }
  return parseFloat(it?.quantity) || 0;
};

// Today as YYYY-MM-DD in the browser's local timezone — the exact form a native
// date input expects. Module scope because the initial useState below runs before
// any helper declared inside the component exists.
const todayISO = () => new Date().toLocaleDateString('en-CA');

const OrdersForm = ({ isOpen, onClose, onSubmit, onSuccess, order = null, orderId = null, loading = false }) => {
  const { t } = useTranslation();
  const strings = t('ordersModule.orders.view', { returnObjects: true });
  const formStrings = t('ordersModule.orders.forms', { returnObjects: true });
  const { resolving, resolveCustomerAddress } = useCustomerDeliveryLocation();
  // Global kill switch for per-coil tracking. False while it loads and false on any
  // fetch error, so the coil UI simply never appears unless the backend says it is on.
  const { coilTrackingEnabled } = useCoilSettings();

  const [currentOrder, setCurrentOrder] = useState(order);
  const lastFetchedIdRef = useRef(null);
  const [showPendingList, setShowPendingList] = useState(false);
  const pendingFileInputRef = useRef(null);

  if (!window.ordersFormCache) window.ordersFormCache = { customers: null, locations: null, productSizes: null, ordersById: {}, loaded: false, loading: false };
  const cache = window.ordersFormCache;

  const [customers, setCustomers] = useState([]);
  // Full detail of the selected customer. The list endpoint omits gst_number,
  // so we fetch the detail on selection to display GST alongside the address.
  const [selectedCustomerDetail, setSelectedCustomerDetail] = useState(null);
  // Open when the customer's address is missing a part (usually city) that must be
  // collected before it can become a delivery location.
  const [showAddressPrompt, setShowAddressPrompt] = useState(false);
  const [locations, setLocations] = useState([]);
  const [productSizes, setProductSizes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const customerSearchRef = useRef(null);
  const customerActiveTermRef = useRef('');
  const productSearchRef = useRef(null);
  const productActiveTermRef = useRef('');
  const supplierSearchRef = useRef(null);
  const formItemsRef = useRef([]);

  const initialMode = order || orderId ? 'existing' : 'existing';
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(0);
  const [newCustomerWarehouses, setNewCustomerWarehouses] = useState([]);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);

  const [formOrder, setFormOrder] = useState(() => ({
    customer_id: order?.customer_id ?? '',
    location_id: order?.location_id ?? '',
    warehouse: order?.warehouse ?? false,
    source_type: order?.source_type ?? 'warehouse',
    selected_warehouse_id: order?.warehouse_id ?? '',
    selected_supplier_id: order?.supplier_id ?? '',
    price_list_date: order?.price_list_date ?? todayISO(),
    expected_delivery_date: order?.expected_delivery_date ?? '',
    freight: order?.freight ?? '',
    freight_type: order?.freight_type ?? '',
    freight_pricing_type: order?.freight_pricing_type ?? 'fixed',
    discount_type: order?.discount_type ?? '',
    discount_value: order?.discount_value ?? '',
    loading_cost_per_kg: order?.loading_cost_per_kg ?? '',
    gst_applicable: order?.gst_applicable ?? false,
    reference_party_name: order?.reference_party_name ?? '',
    payment_terms: order?.payment_terms ?? '',
    remark: order?.remark ?? '',
    order_internal_remark: order?.order_internal_remark ?? '',
    items: Array.isArray(order?.items) && order.items.length > 0 ? order.items.map(i => ({
        product_size_id: i.product_size_id || '', unit: normalizeUnit(i.unit), length: i.length || '', quantity: i.quantity || '', quantity_in_pieces: i.pieces || i.quantity_in_pieces || '', price: (i.price === null || i.price === undefined) ? '' : String(i.price), length_note: i.length_note ?? '', cut_pieces: i.cut_pieces ?? '', remark: i.remark || '', load_by_pieces: !!(i.load_by_pieces), coil_picks: coilPicksFromItem(i)
    })) : [emptyItem()],
  }));

  // build localized default new-customer template using useMemo to avoid stale placeholders
  const defaultNewCustomer = useMemo(() => ({
    owner_name: '',
    company_name: '',
    gst_number: '',
    tally_name: '',
    contact_person: '',
    phone_number_1: '',
    email: '',
    address_line1: '',
    city: '',
    state: '',
    country: t('ordersModule.locations.defaults.country'),
    zip_code: '',
    credit_period: 0,
    reference_party_name: '',
    isAuthorized: true,
    sister_company: false,
  }), [t]);

  const formOrderRef = useRef(formOrder);
  useEffect(() => {
    formOrderRef.current = formOrder;
    formItemsRef.current = formOrder.items;
  }, [formOrder]);



  const [newCustomer, setNewCustomer] = useState(defaultNewCustomer);
  const [errors, setErrors] = useState({});
  const [loadingOrder, setLoadingOrder] = useState(false);
  // Set when an edit-mode detail fetch fails/404s so we show a "couldn't load"
  // message instead of spinning forever (the record may have been deleted).
  const [loadOrderError, setLoadOrderError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [locationAddInitialValue, setLocationAddInitialValue] = useState('');
  const [locationSearchLoading, setLocationSearchLoading] = useState(false);
  const locationSearchRef = useRef(null);
  const [warehouseSearchLoading, setWarehouseSearchLoading] = useState(false);
  const warehouseSearchRef = useRef(null);
  const totalOrderAmount = useMemo(() => {
    return (formOrder.items || []).reduce((sum, item) => {
      const q = amountBasisQty(item);  // pieces for a pcs line, else quantity
      const p = parseFloat(item.price) || 0;
      return sum + (q * p);
    }, 0);
  }, [formOrder.items]);

  // Fetch the selected customer's full detail so GST + complete address can be
  // shown under the dropdown. The list endpoint only returns summary fields
  // (city/state), not gst_number or address lines.
  useEffect(() => {
    const id = formOrder.customer_id;
    if (mode !== 'existing' || !id) {
      setSelectedCustomerDetail(null);
      return;
    }
    let cancelled = false;
    customersService.getCustomerById(id).then((res) => {
      if (!cancelled && res?.success && res.data) {
        setSelectedCustomerDetail(res.data);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [formOrder.customer_id, mode]);

  const totalOrderWeight = useMemo(() => {
    return (formOrder.items || []).reduce((sum, it) => {
      const itUnit = (it.unit || '').toLowerCase();
      const qty = parseFloat(it.quantity) || 0;

      const isOriginalProduct = it.original_product_size_id && String(it.product_size_id) === String(it.original_product_size_id);
      
      let pWeight = 0;
      let sLength = 0;

      if (isOriginalProduct && it.standard_weight && it.standard_length) {
        pWeight = parseFloat(it.standard_weight) || 0;
        sLength = parseFloat(it.standard_length) || 1;
      } else {
        const product = (productSizes || []).find(p => String(p.id) === String(it.product_size_id)) || (cache.productSizes || []).find(p => String(p.id) === String(it.product_size_id));
        if (product) {
          pWeight = parseFloat(product.standard_weight || product.product_weight) || 0;
          sLength = parseFloat(product.standard_length) || 1;
        }
      }

      // kg, or a pieces line whose `quantity` carries the optional kg weight:
      // weight is the quantity itself, independent of product specs.
      if (isWeightUnit(itUnit) || isPieceUnit(itUnit)) {
        return sum + qty;
      }
      if (pWeight > 0 && sLength > 0 && isLengthUnit(itUnit)) {
        const approxW = (pWeight / sLength) * qty;
        return sum + (Number.isFinite(approxW) ? approxW : 0);
      }
      return sum;
    }, 0);
  }, [formOrder.items, productSizes, cache.productSizes]);

  // helper to safely get label text from translation nodes which may be either string or object with `label`
  const labelText = (node) => (typeof node === 'string' ? node : node?.label ?? '');
  const fetchOrderById = useCallback(async (id) => {
    if (!id) return null;
    if (lastFetchedIdRef.current === id) return null;
    lastFetchedIdRef.current = id;
    try {
      // If we've already fetched this order earlier, use the cached copy
      if (cache.ordersById && cache.ordersById[id]) {
        const o = cache.ordersById[id];

        // Map API order shape into formOrder shape used by this component
        const mapped = {
          customer_id: o.customer_id ?? o.customerId ?? '',
          location_id: o.location_id ?? o.locationId ?? o.delivery_location_id ?? '',
          warehouse: !!o.warehouse,
          source_type: o.source_type ?? '',
          selected_warehouse_id: o.warehouse_id ?? '',
          selected_supplier_id: o.supplier_id ?? o.supplierId ?? '',
          price_list_date: o.price_list_date ?? '',
          expected_delivery_date: o.expected_delivery_date ?? o.expectedDeliveryDate ?? '',
          freight: o.freight !== undefined && o.freight !== null ? String(o.freight) : '',
          freight_type: o.freight_type ?? '',
          freight_pricing_type: o.freight_pricing_type ?? 'fixed',
          discount_type: o.discount_type ?? o.discountType ?? '',
          discount_value: o.discount_value !== undefined && o.discount_value !== null ? String(o.discount_value) : '',
          loading_cost_per_kg: o.loading_cost_per_kg !== undefined && o.loading_cost_per_kg !== null ? String(o.loading_cost_per_kg) : '',
          gst_applicable: !!(o.gst_applicable ?? o.gst_included),
          reference_party_name: o.reference_party_name ?? '',
          payment_terms: o.payment_terms ?? '',
          remark: o.remark ?? '',
          order_internal_remark: o.order_internal_remark ?? '',
          items: Array.isArray(o.items) && o.items.length > 0 ? o.items.map(it => ({
            product_size_id: it.product_size_id ?? it.size_id ?? it.productSizeId ?? it.sizeId ?? '',
            product_name: it.product_name || it.product?.name || '',
            size_name: it.size_name || it.alias_name || '',
            unit: normalizeUnit(it.unit ?? it.unit_name) || 'kg',
            length: it.length !== undefined && it.length !== null ? String(it.length) : '',
            quantity: it.quantity !== undefined && it.quantity !== null ? String(it.quantity) : '',
            quantity_in_pieces: it.quantity_in_pieces ?? it.pieces ?? it.quantityInPieces ?? it.quantity ?? '',
            cut_pieces: it.cut_pieces ?? '',
            price: it.price !== undefined && it.price !== null ? String(it.price) : '',
            length_note: it.length_note ?? '',
            remark: it.remark ?? it.item_remark ?? '',
            load_by_pieces: !!(it.load_by_pieces),
            standard_weight: it.standard_weight || it.product_weight || '',
            standard_length: it.standard_length || '',
            original_product_size_id: it.product_size_id ?? it.size_id ?? it.productSizeId ?? it.sizeId ?? '',
          })) : [emptyItem()],
        };

        setCurrentOrder(o);
        setFormOrder(mapped);
        return o;
      }

      // Try to fetch order from service (force fresh)
      const result = await ordersService.getOrderById(id, { force: true });
      if (!result || !result.success || !result.data) {
        lastFetchedIdRef.current = null;  // clear so a reopen retries instead of re-returning null
        return null;
      }
      const o = result.data;

      // cache raw order data so future opens use the local copy
      cache.ordersById = cache.ordersById || {};
      cache.ordersById[id] = o;

      // Map API order shape into formOrder shape used by this component
      const mapped = {
        customer_id: o.customer_id ?? o.customerId ?? '',
        location_id: o.location_id ?? o.locationId ?? o.delivery_location_id ?? '',
        warehouse: !!o.warehouse,
        source_type: o.source_type ?? '',
        selected_warehouse_id: o.warehouse_id ?? '',
        selected_supplier_id: o.supplier_id ?? o.supplierId ?? '',
        expected_delivery_date: o.expected_delivery_date ?? o.expectedDeliveryDate ?? '',
        freight: o.freight !== undefined && o.freight !== null ? String(o.freight) : '',
        freight_type: o.freight_type ?? '',
        freight_pricing_type: o.freight_pricing_type ?? 'fixed',
        discount_type: o.discount_type ?? o.discountType ?? '',
        discount_value: o.discount_value !== undefined && o.discount_value !== null ? String(o.discount_value) : '',
        loading_cost_per_kg: o.loading_cost_per_kg !== undefined && o.loading_cost_per_kg !== null ? String(o.loading_cost_per_kg) : '',
        gst_applicable: !!(o.gst_applicable ?? o.gst_included),
        reference_party_name: o.reference_party_name ?? '',
        payment_terms: o.payment_terms ?? '',
        remark: o.remark ?? '',
        items: Array.isArray(o.items) && o.items.length > 0 ? o.items.map(it => ({
          product_size_id: it.product_size_id ?? it.size_id ?? it.productSizeId ?? it.sizeId ?? '',
          product_name: it.product_name || it.product?.name || '',
          size_name: it.size_name || it.alias_name || '',
          unit: normalizeUnit(it.unit ?? it.unit_name) || 'kg',
          length: it.length !== undefined && it.length !== null ? String(it.length) : '',
          quantity: it.quantity !== undefined && it.quantity !== null ? String(it.quantity) : '',
          quantity_in_pieces: it.quantity_in_pieces ?? it.pieces ?? it.quantityInPieces ?? it.quantity ?? '',
          price: it.price !== undefined && it.price !== null ? String(it.price) : '',
          length_note: it.length_note ?? '',
          remark: it.remark ?? it.item_remark ?? '',
          load_by_pieces: !!(it.load_by_pieces),
          standard_weight: it.standard_weight || it.product_weight || '',
          standard_length: it.standard_length || '',
          original_product_size_id: it.product_size_id ?? it.size_id ?? it.productSizeId ?? it.sizeId ?? '',
        })) : [emptyItem()],
      };

      setCurrentOrder(o);
      setFormOrder(mapped);
      return o;
    } catch (err) {
      // swallow and return null; caller will handle missing order
      lastFetchedIdRef.current = null;  // clear so a reopen retries
      return null;
    }
  }, [cache]);

  // SKUs are never served from the window cache: a price edited in Product Sizes
  // must show on the very next form open (P1-4). Customers/locations/warehouses
  // keep the cached seed.
  const refreshProductSizes = useCallback(async () => {
    try {
      const resp = await productSizesService.getAllProductSizes({ limit: 20 }, { force: true });
      const raw = resp?.data;
      const data = Array.isArray(raw) ? raw
        : (Array.isArray(raw?.items)) ? raw.items
        : (Array.isArray(raw?.product_sizes)) ? raw.product_sizes : [];
      cache.productSizes = data;
      // A search typed meanwhile owns the dropdown; the cache is enough for it.
      if (!productActiveTermRef.current) setProductSizes(data);
    } catch (e) { /* keep whatever is already shown */ }
  }, [cache]);

  const loadDropdowns = useCallback(async () => {
    // If all datasets are already loading or loaded, nothing to do
    if (cache.loading || cache.loaded) {
      // Even if loaded, ensure local state matches cache if state is empty
      if (cache.loaded) {
        if (locations.length === 0 && cache.locations) setLocations(cache.locations);
        if (warehouses.length === 0 && cache.warehouses) setWarehouses(cache.warehouses || []);
        if (suppliers.length === 0 && cache.suppliers) setSuppliers(cache.suppliers || []);
        refreshProductSizes();
      }
      return;
    }

    // Check if everything is in cache
    const hasCustomers = Array.isArray(cache.customers) && cache.customers.length > 0;
    const hasLocations = Array.isArray(cache.locations) && cache.locations.length > 0;
    const hasProductSizes = Array.isArray(cache.productSizes) && cache.productSizes.length > 0;
    const hasWarehouses = Array.isArray(cache.warehouses) && cache.warehouses.length > 0;
    const hasSuppliers = Array.isArray(cache.suppliers) && cache.suppliers.length > 0;

    if (hasCustomers && hasLocations && hasProductSizes && hasWarehouses && hasSuppliers) {
      setCustomers(cache.customers);
      setLocations(cache.locations);
      setProductSizes(cache.productSizes);
      setWarehouses(cache.warehouses);
      setSuppliers(cache.suppliers);
      cache.loaded = true;
      refreshProductSizes(); // SKUs are never trusted from the cache (P1-4)
      return;
    }

    cache.loading = true;
    setDropdownsLoading(true);

    try {
      const fetchers = [];
      const keys = [];

      if (!hasCustomers) { fetchers.push(customersService.getCustomers({ limit: 20, is_active: true }).catch(() => ({ data: [] }))); keys.push('customers'); }
      if (!hasLocations) { fetchers.push(locationsService.getLocations().catch(() => ({ data: [] }))); keys.push('locations'); }
      // SKUs are always fetched fresh: the list cache kept a price edited minutes ago
      // out of the form until the session expired (P1-4).
      if (!hasProductSizes) { fetchers.push(productSizesService.getAllProductSizes({ limit: 20 }, { force: true }).catch(() => ({ data: [] }))); keys.push('productSizes'); }
      if (!hasWarehouses) { fetchers.push(warehouseService.getAllWarehouses().catch(() => ({ data: [] }))); keys.push('warehouses'); }
      if (!hasSuppliers) { fetchers.push(customersService.getCustomers({ limit: 20, is_active: true }).catch(() => ({ data: [] }))); keys.push('suppliers'); }

      const results = await Promise.all(fetchers);

      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const resp = results[i];
        const rawData = resp?.data;
        const data = Array.isArray(rawData) ? rawData
          : (rawData?.items && Array.isArray(rawData.items)) ? rawData.items
          : (rawData?.product_sizes && Array.isArray(rawData.product_sizes)) ? rawData.product_sizes
          : Array.isArray(resp) ? resp : [];
        cache[key] = data;
        if (key === 'customers') setCustomers(data);
        if (key === 'locations') setLocations(data);
        if (key === 'productSizes') setProductSizes(data);
        if (key === 'warehouses') setWarehouses(data || []);
        if (key === 'suppliers') setSuppliers(data || []);
      }

      if (Array.isArray(cache.customers) && Array.isArray(cache.locations) && Array.isArray(cache.productSizes) && Array.isArray(cache.warehouses)) {
        cache.loaded = true;
      }
    } catch (e) {
      console.error('Failed to load dropdowns', e);
    } finally {
      cache.loading = false;
      setDropdownsLoading(false);
    }
  }, [cache, locations.length, warehouses.length, suppliers.length, refreshProductSizes]); // Stable identity enough; decoupled from effects that fetch data

  const handleCustomerSearch = useCallback((term) => {
    if (customerSearchRef.current) clearTimeout(customerSearchRef.current);
    customerActiveTermRef.current = term;
    if (!term.trim()) {
      setCustomerSearchLoading(false);
      setCustomers((prev) => {
        const base = cache.customers || [];
        const selectedId = String(formOrderRef.current?.customer_id || '');
        if (selectedId && !base.some(c => String(c.id) === selectedId)) {
          const selected = prev.find(c => String(c.id) === selectedId);
          return selected ? [selected, ...base] : base;
        }
        return base;
      });
      return;
    }
    setCustomerSearchLoading(true);
    customerSearchRef.current = setTimeout(async () => {
      try {
        const resp = await customersService.getCustomers({ search: term, limit: 20, is_active: true });
        if (customerActiveTermRef.current !== term) return;
        const rawData = resp?.data;
        const data = Array.isArray(rawData) ? rawData
          : (rawData?.items && Array.isArray(rawData.items)) ? rawData.items
          : (rawData?.customers && Array.isArray(rawData.customers)) ? rawData.customers
          : [];
        setCustomers((prev) => {
          const selectedId = String(formOrderRef.current?.customer_id || '');
          if (selectedId) {
            const selected = prev.find(c => String(c.id) === selectedId);
            if (selected && !data.some(d => String(d.id) === selectedId)) {
              return [selected, ...data];
            }
          }
          return data;
        });
      } catch (e) {
        // ignore search errors
      } finally {
        if (customerActiveTermRef.current === term) setCustomerSearchLoading(false);
      }
    }, 400);
  }, [cache]);

  const handleLocationSearch = useCallback((term) => {
    if (locationSearchRef.current) clearTimeout(locationSearchRef.current);
    if (!term.trim()) {
      setLocationSearchLoading(false);
      setLocations((prev) => {
        const base = cache.locations || prev;
        const selectedId = String(formOrderRef.current?.location_id || '');
        if (selectedId && !base.some((c) => String(c.id) === String(selectedId))) {
          const selected = prev.find((c) => String(c.id) === String(selectedId));
          return selected ? [selected, ...base] : base;
        }
        return base;
      });
      return;
    }
    setLocationSearchLoading(true);
    locationSearchRef.current = setTimeout(async () => {
      try {
        const response = await locationsService.getLocations({ search: term, limit: 20 });
        const list = Array.isArray(response?.data) ? response.data : [];
        setLocations((prev) => {
          const selectedId = String(formOrderRef.current?.location_id || '');
          if (selectedId) {
            const selected = prev.find((c) => String(c.id) === String(selectedId));
            if (selected && !list.some((c) => String(c.id) === String(selectedId))) {
              return [selected, ...list];
            }
          }
          return list;
        });
      } catch (e) {
      } finally {
        setLocationSearchLoading(false);
      }
    }, 400);
  }, [cache]);

  const handleWarehouseSearch = useCallback((term) => {
    if (warehouseSearchRef.current) clearTimeout(warehouseSearchRef.current);
    if (!term.trim()) {
      setWarehouseSearchLoading(false);
      setWarehouses((prev) => {
        const base = cache.warehouses || prev;
        const selectedId = String(formOrderRef.current?.selected_warehouse_id || '');
        if (selectedId && !base.some((w) => String(w.id) === String(selectedId))) {
          const selected = prev.find((w) => String(w.id) === String(selectedId));
          return selected ? [selected, ...base] : base;
        }
        return base;
      });
      return;
    }
    setWarehouseSearchLoading(true);
    warehouseSearchRef.current = setTimeout(async () => {
      try {
        const response = await warehouseService.getAllWarehouses({ search: term, limit: 20 });
        const list = Array.isArray(response?.data) ? response.data : [];
        setWarehouses((prev) => {
          const selectedId = String(formOrderRef.current?.selected_warehouse_id || '');
          if (selectedId) {
            const selected = prev.find((w) => String(w.id) === String(selectedId));
            if (selected && !list.some((w) => String(w.id) === String(selectedId))) {
              return [selected, ...list];
            }
          }
          return list;
        });
      } catch (e) {
      } finally {
        setWarehouseSearchLoading(false);
      }
    }, 400);
  }, [cache]);

  const handleProductSearch = useCallback((term) => {
    if (productSearchRef.current) clearTimeout(productSearchRef.current);
    productActiveTermRef.current = term;
    if (!term.trim()) {
      setProductSearchLoading(false);
      setProductSizes((prev) => {
        const base = cache.productSizes || [];
        const selectedIds = new Set((formItemsRef.current || []).map(it => String(it.product_size_id)).filter(id => id));
        
        if (selectedIds.size > 0) {
          const missing = Array.from(selectedIds).filter(id => !base.some(p => String(p.id) === id));
          if (missing.length > 0) {
            const selectedItems = prev.filter(p => selectedIds.has(String(p.id)));
            return [...selectedItems, ...base.filter(p => !selectedIds.has(String(p.id)))];
          }
        }
        return base;
      });
      return;
    }
    setProductSearchLoading(true);
    productSearchRef.current = setTimeout(async () => {
      try {
        const resp = await productSizesService.getAllProductSizes({ search: term, limit: 20 }, { force: true });
        if (productActiveTermRef.current !== term) return;
        const rawData = resp?.data;
        const data = Array.isArray(rawData) ? rawData
          : (rawData?.items && Array.isArray(rawData.items)) ? rawData.items
          : (rawData?.product_sizes && Array.isArray(rawData.product_sizes)) ? rawData.product_sizes
          : [];
        setProductSizes((prev) => {
          // Add newly found products to the cache if not already present
          if (cache.productSizes) {
            const existingIds = new Set(cache.productSizes.map(p => String(p.id)));
            const newProducts = data.filter(p => !existingIds.has(String(p.id)));
            if (newProducts.length > 0) {
              cache.productSizes = [...cache.productSizes, ...newProducts];
            }
          }
          return data;
        });
      } catch (e) {
        // ignore search errors
      } finally {
        if (productActiveTermRef.current === term) setProductSearchLoading(false);
      }
    }, 400);
  }, [cache]);

  const handleSupplierSearch = useCallback((term) => {
    if (supplierSearchRef.current) clearTimeout(supplierSearchRef.current);
    supplierSearchRef.current = setTimeout(async () => {
      if (!term.trim()) {
        setSupplierSearchLoading(false);
        setSuppliers((prev) => {
          const base = cache.suppliers || prev;
          const selectedId = String(formOrderRef.current?.selected_supplier_id || '');
          if (selectedId && !base.some(s => String(s.id) === selectedId)) {
            const selected = prev.find(s => String(s.id) === selectedId);
            return selected ? [selected, ...base] : base;
          }
          return base;
        });
        return;
      }
      setSupplierSearchLoading(true);
      try {
        const resp = await customersService.getCustomers({ search: term, limit: 20, is_active: true });
        const data = Array.isArray(resp?.data) ? resp.data : [];
        setSuppliers((prev) => {
          const selectedId = String(formOrderRef.current?.selected_supplier_id || '');
          if (selectedId) {
            const selected = prev.find(s => String(s.id) === selectedId);
            if (selected && !data.some(d => String(d.id) === selectedId)) {
              return [selected, ...data];
            }
          }
          return data;
        });
      } catch (e) {
        // ignore search errors
      } finally {
        setSupplierSearchLoading(false);
      }
    }, 400);
  }, [cache]);

  const handleAddLocationSuccess = (newLocation) => {
    setLocations(prev => {
        const hasIt = prev.some(l => l.id === newLocation.id);
        if (hasIt) return prev;
        return [...prev, newLocation];
    });
    if (cache.locations) {
        if (!cache.locations.some(l => l.id === newLocation.id)) {
            cache.locations = [...cache.locations, newLocation];
        }
    }
    setFormOrder(prev => ({ ...prev, location_id: newLocation.id }));
  };

  useEffect(() => {
    if (!isOpen) {
      lastFetchedIdRef.current = null;
      setCurrentOrder(order || null);
    }
  }, [isOpen, order]);

  // Helper to get tomorrow's date in YYYY-MM-DD format
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const y = tomorrow.getFullYear();
    const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const d = String(tomorrow.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Effect 1: Fetching data (Order + Dropdowns)
  useEffect(() => {
    if (!isOpen) {
      setLoadingOrder(false);
      return;
    }

    const fetchData = async () => {
      // If we are editing (orderId present but no order object yet)
      if (orderId && !order && !currentOrder) {
        // Clear specific order from cache if we want fresh data on open
        if (cache.ordersById && cache.ordersById[orderId]) {
          delete cache.ordersById[orderId];
        }

        try {
          setLoadingOrder(true);
          setLoadOrderError(false);
          const fetched = await fetchOrderById(orderId);
          await loadDropdowns();
          if (fetched) {
            setCurrentOrder(fetched);
          } else {
            // 404 / deleted / network failure — show an error instead of an
            // endless spinner (currentOrder stays null otherwise).
            setLoadOrderError(true);
          }
        } finally {
          setLoadingOrder(false);
        }
      } else {
        // Just load dropdowns for new orders or when order is already provided
        loadDropdowns();
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId]); // Only run when modal opens or orderId changes

  // Effect 2: Syncing form state with current order data
  useEffect(() => {
    if (!isOpen) return;

    const active = currentOrder || order;
    if (active) {
      setMode('existing');
      setFormOrder({
        customer_id: active.customer_id ?? '',
        location_id: active.location_id ?? '',
        warehouse: active.warehouse ?? false,
        source_type: active.source_type ?? 'warehouse',
        selected_warehouse_id: active.warehouse_id ?? '',
        selected_supplier_id: active.supplier_id ?? '',
        price_list_date: active.price_list_date ?? '',
        expected_delivery_date: active.expected_delivery_date ?? getTomorrowDate(),
        freight_type: active.freight_type ?? '',
        freight_pricing_type: active.freight_pricing_type ?? 'fixed',
        discount_type: active.discount_type ?? '',
        discount_value: active.discount_value ?? '',
        loading_cost_per_kg: active.loading_cost_per_kg ?? '',
        gst_applicable: !!(active.gst_applicable ?? active.gst_included),
        reference_party_name: active.reference_party_name ?? '',
        payment_terms: active.payment_terms ?? '',
        remark: active.remark ?? '',
        order_internal_remark: active.order_internal_remark ?? '',
        items: Array.isArray(active.items) && active.items.length > 0 ? active.items.map(i => ({
          product_size_id: i.product_size_id || '',
          product_name: i.product_name || i.product?.name || '',
          size_name: i.size_name || i.alias_name || '',
          unit: normalizeUnit(i.unit) || 'kg',
          length: i.length || '',
          quantity: i.quantity || '',
          quantity_in_pieces: i.quantity_in_pieces ?? i.pieces ?? i.quantityInPieces ?? i.quantity ?? '',
          cut_pieces: i.cut_pieces ?? '',
          price: (i.price === null || i.price === undefined) ? '' : String(i.price),
          length_note: i.length_note || '',
          remark: i.remark || '',
          load_by_pieces: !!(i.load_by_pieces),
          standard_weight: i.standard_weight || i.product_weight || '',
          standard_length: i.standard_length || '',
          original_product_size_id: i.product_size_id || '',
          coil_picks: coilPicksFromItem(i),
        })) : [emptyItem()],
      });
    } else {
      // Reset for new order
      setStep(0);
      setMode(initialMode);
      setFormOrder({
        customer_id: '',
        location_id: '',
        warehouse: false,
        source_type: 'warehouse',
        selected_warehouse_id: '',
        selected_supplier_id: '',
        price_list_date: todayISO(),
        expected_delivery_date: getTomorrowDate(),
        freight_type: '',
        freight_pricing_type: 'fixed',
        discount_type: '',
        discount_value: '',
        loading_cost_per_kg: '',
        gst_option: '',
        gstn_number: '',
        gst_percentage: '',
        reference_party_name: '',
        payment_terms: '',
        remark: '',
        order_internal_remark: '',
        items: [emptyItem()],
      });
      setNewCustomer(defaultNewCustomer);
      setPendingFiles([]);
    }
    setErrors({});
  }, [isOpen, order, currentOrder, initialMode, defaultNewCustomer]);


  const handleFormOrderChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;

    // "Use customer's address" is an action, not a location. Intercept it before it can
    // reach form state: every submit path does `Number(location_id) || 0`, which would
    // turn the sentinel into a silent 0.
    if (name === 'location_id' && isSyntheticLocationValue(v)) {
      handleCustomerAddressPick();
      return;
    }

    if (['freight', 'discount_value', 'loading_cost_per_kg', 'price', 'quantity', 'length', 'quantity_in_pieces', 'selected_warehouse_id', 'selected_supplier_id'].includes(name)) v = sanitizeNumber(v);

    setFormOrder(prev => {
      const next = { ...prev, [name]: v };

      // Coil stock is warehouse-scoped, so moving the order to another warehouse — or to
      // a supplier, which has no coil stock at all — invalidates every pick on it.
      // Carrying them over would submit coil ids belonging to a warehouse the order no
      // longer ships from, and nothing surfaces that until challan time.
      const warehouseChanged = name === 'selected_warehouse_id' && String(v) !== String(prev.selected_warehouse_id ?? '');
      const sourceChanged = name === 'source_type' && v !== prev.source_type;
      if (warehouseChanged || sourceChanged) {
        next.items = (prev.items || []).map(item => (item.coil_picks?.length ? { ...item, coil_picks: [] } : item));
      }

      // Auto-fill delivery location if customer is selected
      if (name === 'customer_id' && v) {
        const selectedCustomer = customers.find(c => String(c.id) === String(v));
        if (selectedCustomer && Array.isArray(selectedCustomer.warehouses)) {
          const defaultWh = selectedCustomer.warehouses.find(w => w.is_default);
          if (defaultWh && defaultWh.location_id) {
            next.location_id = defaultWh.location_id;
          }
          if (selectedCustomer && selectedCustomer.reference_party_name) {
            next.reference_party_name = selectedCustomer.reference_party_name;
          }
        }
      }
      return next;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleNewCustomerChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;
    if (name === 'phone_number_1') {
      v = value.replace(/\D/g, '').slice(0, 10);
    }
    setNewCustomer(prev => ({ ...prev, [name]: v }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleItemChange = (index, field, value) => {
    setFormOrder(prev => {
      const items = Array.isArray(prev.items) ? [...prev.items] : [];
      const updatedItem = { ...items[index], [field]: ['quantity', 'price', 'length', 'quantity_in_pieces', 'cut_pieces'].includes(field) ? sanitizeNumber(value) : value };

      // Picks belong to the product they were made against — a coil of the old size is
      // not a coil of the new one. Dropped here rather than at submit time so the strip
      // and the payload can never disagree about what the line is holding.
      if (field === 'product_size_id') {
        updatedItem.coil_picks = [];

        updatedItem.length_note = '';

        // Auto-fill the line price from the selected SKU's master price
        // (migration 061: price now lives on the product_size). An unpriced
        // SKU leaves the line blank; the operator can still edit/clear it.
        const byId = (p) => String(p.id) === String(value);
        const selectedSku = (productSizes || []).find(byId) || (cache.productSizes || []).find(byId);
        let skuPrice = selectedSku ? (selectedSku.base_price ?? selectedSku.price) : null;
        if ((skuPrice === null || skuPrice === undefined) && currentOrder?.items) {
          // Edit mode: an SKU known only through another line of this order (outside the
          // first page of the list) carries that line's price.
          const sibling = currentOrder.items.find(ci => String(ci.product_size_id ?? ci.size_id) === String(value));
          if (sibling && sibling.price !== null && sibling.price !== undefined) skuPrice = sibling.price;
        }
        updatedItem.price = (skuPrice !== null && skuPrice !== undefined && skuPrice !== '')
          ? String(skuPrice)
          : '';
      }

      // Calculate Pieces or Quantity based on each other and product specifications
      const isOriginalProduct = updatedItem.original_product_size_id && String(updatedItem.product_size_id) === String(updatedItem.original_product_size_id);
      
      let pWeight = 0;
      let sLength = 0;

      if (isOriginalProduct && updatedItem.standard_weight && updatedItem.standard_length) {
        // Use historical specs from the order item
        pWeight = parseFloat(updatedItem.standard_weight) || 0;
        sLength = parseFloat(updatedItem.standard_length) || 1;
      } else {
        // Use current specs from product catalogue
        const product = productSizes.find(p => String(p.id) === String(updatedItem.product_size_id));
        if (product) {
          pWeight = parseFloat(product.standard_weight || product.product_weight) || 0;
          sLength = parseFloat(product.standard_length) || 1;
        }
      }

      // The piece arithmetic below converts between quantity and pieces through the
      // product master's weight-per-standard-length. That has no meaning for a coil: its
      // "pieces" are coils, its quantity is the weight picked off them, and the master
      // carries zeros for these SKUs anyway. Applying it turned a 50 kg line into 4
      // pieces, and then a 2-piece correction back into 24 kg.
      const coilLine = isCoilTrackedLine(updatedItem);

      if (!coilLine && pWeight > 0 && sLength > 0) {
        const rowLength = parseFloat(updatedItem.length) || sLength;
        const weightPerPiece = (pWeight / sLength) * rowLength;

        // Ensure names are captured if they exist on the product object
        const product = productSizes.find(p => String(p.id) === String(updatedItem.product_size_id));
        if (product) {
          updatedItem.product_name = product.product_name || product.name || updatedItem.product_name || '';
          updatedItem.size_name = product.alias_name || product.size_name || updatedItem.size_name || '';
        }

        if (field === 'quantity') {
          const qty = parseFloat(sanitizeNumber(value)) || 0;
          if (isWeightUnit(updatedItem.unit) && weightPerPiece > 0) {
            updatedItem.quantity_in_pieces = String(Math.round(qty / weightPerPiece));
          } else if (isLengthUnit(updatedItem.unit) && rowLength > 0) {
            updatedItem.quantity_in_pieces = String(Math.round(qty / rowLength));
          }
        } else if (field === 'quantity_in_pieces') {
          const pcs = parseFloat(sanitizeNumber(value)) || 0;
          if (isWeightUnit(updatedItem.unit) && weightPerPiece > 0) {
            updatedItem.quantity = sanitizeNumber((pcs * weightPerPiece).toFixed(3));
          } else if (isLengthUnit(updatedItem.unit) && rowLength > 0) {
            updatedItem.quantity = sanitizeNumber((pcs * rowLength).toFixed(3));
          }
        } else if (['unit', 'product_size_id', 'length'].includes(field)) {
          // If unit, product or length changes, recalculate quantity from pieces to maintain physical count
          const pcs = sanitizeNumber(updatedItem.quantity_in_pieces);
          if (pcs && !isNaN(pcs) && pcs !== '') {
            if (isWeightUnit(updatedItem.unit) && weightPerPiece > 0) {
              updatedItem.quantity = sanitizeNumber((pcs * weightPerPiece).toFixed(3));
            } else if (isLengthUnit(updatedItem.unit) && rowLength > 0) {
              updatedItem.quantity = sanitizeNumber((pcs * rowLength).toFixed(3));
            }
          }
        }
      }

      items[index] = updatedItem;

      // Auto-add next line if this is the last item and all mandatory fields are filled (Add mode only)
      const isAddMode = !order && !currentOrder;
      if (isAddMode && index === items.length - 1) {
        // price intentionally omitted — it is optional now (migration 061), so a
        // line left unpriced should still spawn the next row.
        const isFilled = updatedItem.product_size_id &&
                         updatedItem.quantity &&
                         updatedItem.unit &&
                         updatedItem.length &&
                         parseFloat(updatedItem.quantity) > 0;
        
        if (isFilled) {
          items.push(emptyItem());
        }
      }

      return { ...prev, items };
    });

    // Clear any item-level validation error for this specific field (e.g. "items.0.quantity")
    const errKey = `items.${index}.${field}`;
    setErrors(prev => {
      if (!prev || !prev[errKey]) return prev || {};
      const copy = { ...prev };
      delete copy[errKey];
      return copy;
    });
  };

  const handleCoilPicksChange = (index, picks) => {
    setFormOrder(prev => {
      const items = Array.isArray(prev.items) ? [...prev.items] : [];
      if (!items[index]) return prev;
      const next = Array.isArray(picks) ? picks : [];
      items[index] = {
        ...items[index],
        coil_picks: next,
        // A coil is a roll, not a piece cut to a standard length, so "pieces" on this
        // line is simply how many coils are on it.
        quantity_in_pieces: next.length ? String(next.length) : '',
      };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setFormOrder(prev => {
      const items = Array.isArray(prev.items) ? [...prev.items] : [];
      const lastIndex = Math.max(0, items.length - 1);
      const lastItem = items[lastIndex] || emptyItem();

      // Validate the last item using centralized validator; remap returned keys to actual index
      const itemValidation = validateOrderItems([lastItem]);
      if (itemValidation && Object.keys(itemValidation).length > 0) {
        const remapped = {};
        Object.keys(itemValidation).forEach(k => {
          // keys from validator will be like 'items.0.field'
          const newKey = k.replace('items.0.', `items.${lastIndex}.`);
          remapped[newKey] = itemValidation[k];
        });
        setErrors(prevErr => ({ ...prevErr, ...remapped }));
        return prev; // don't add new item until last is valid
      }

      return { ...prev, items: [...items, emptyItem()] };
    });
  };
  const removeItem = (idx) => setFormOrder(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

  const handleRemoveItem = (idx) => {
    const it = formOrder.items[idx];
    const isExisting = !!(it?.original_product_size_id) && (order || orderId || currentOrder);
    if (isExisting) {
      setErrors(prev => ({ ...prev, submit: "Items can't be removed from an existing order. Set quantity to 0 to skip the line at loading time instead." }));
      return;
    }
    removeItem(idx);
  };

  const validate = () => {
    let validationErrors = {};
    const isEdit = !!(orderId || currentOrder || order);

    // Tag each line with whether it's coil-tracked so the shared item validator
    // (which has no access to product data) can apply the coil-only cut-pieces
    // rule. Non-mutating copy — never touch the form state here.
    const formForValidate = {
      ...formOrder,
      items: (formOrder.items || []).map(it => ({ ...it, __coilTracked: isCoilTrackedLine(it) })),
    };

    if (mode === 'existing') {
      // Validate existing customer order
      validationErrors = validateExistingCustomerOrder(formForValidate, { isEdit });
    } else {
      // For new customer mode:
      // - Step 0 (customer info): validate customer data
      // - Step 1 (warehouse info): validate customer data (or skip order validation)
      // - Step 2 (order details): validate both customer data and order data
      if (step === 0 || step === 1) {
        validationErrors = validateNewCustomerData(newCustomer);
      } else {
        validationErrors = validateNewCustomerOrder(formForValidate, newCustomer, { isEdit });
      }
    }

    // Ensure source_type and corresponding id are provided
    if (mode === 'existing' || (mode === 'new' && step === 2)) {
      if (!formOrder.source_type) {
        validationErrors = { ...validationErrors, source_type: t('ordersModule.orders.forms.errors.sourceTypeRequired') };
      } else if (formOrder.source_type === 'warehouse') {
        if (!formOrder.selected_warehouse_id) validationErrors = { ...validationErrors, selected_warehouse_id: t('ordersModule.orders.forms.errors.warehouseRequired') };
      } else if (formOrder.source_type === 'supplier') {
        if (!formOrder.selected_supplier_id) validationErrors = { ...validationErrors, selected_supplier_id: t('ordersModule.orders.forms.errors.supplierRequired') };
      }
    }

    // Payment terms mandatory for sales cycle
    if (mode === 'existing' || (mode === 'new' && step === 2)) {
      if (!formOrder.payment_terms) {
        validationErrors = { ...validationErrors, payment_terms: 'Payment terms is required' };
      }
      // Price list date is mandatory on new sales orders.
      if (!formOrder.price_list_date) {
        validationErrors = { ...validationErrors, price_list_date: 'Price list date is required' };
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleNext = () => {
    if (!validate()) return;
    if (step === 0 && mode === 'new') {
      setStep(1); // Go to Warehouse Step
    } else if (step === 1 && mode === 'new') {
      setStep(2); // Go to Order Details Step
    } else {
      setStep(1); // Existing mode Step 1
    }
  };

  const handleBack = () => {
    setStep((prev) => Math.max(0, prev - 1));
    setErrors({}); // Clear errors when going back
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (submitting) return;            // guard double-submit
    if (!validate()) return;

    setSubmitting(true);
    try {
      if (mode === 'existing') {
        const payload = {
          customer_id: Number(formOrder.customer_id),
          location_id: Number(formOrder.location_id) || 0,
          freight: (formOrder.freight !== '' && formOrder.freight !== null) ? parseFloat(formOrder.freight) : 0,
          freight_type: formOrder.freight_type || null,
          freight_pricing_type: formOrder.freight_pricing_type || 'fixed',
          price_list_date: formOrder.price_list_date || null,
          expected_delivery_date: formOrder.expected_delivery_date || null,
          discount_type: formOrder.discount_type || 'percentage',
          discount_value: (formOrder.discount_value !== '' && formOrder.discount_value !== null) ? parseFloat(formOrder.discount_value) : 0,
          gst_included: !!formOrder.gst_applicable,
          loading_cost_per_kg: (formOrder.loading_cost_per_kg !== '' && formOrder.loading_cost_per_kg !== null) ? parseFloat(formOrder.loading_cost_per_kg) : 0,
          remark: formOrder.remark || '',
          order_internal_remark: formOrder.order_internal_remark || '',
          reference_party_name: formOrder.reference_party_name || '',
          payment_terms: formOrder.payment_terms ? String(formOrder.payment_terms) : null,
          items: (formOrder.items || []).filter(it => it.product_size_id).map(it => ({
            product_size_id: Number(it.product_size_id),
            unit: it.unit || '',
            length: (it.length !== '' && it.length !== null) ? parseFloat(it.length) : 0,
            length_note: isCoilProductLine(it) ? (it.length_note || null) : null,
            quantity: (it.quantity !== '' && it.quantity !== null) ? parseFloat(it.quantity) : 0,
            quantity_in_pieces: it.quantity_in_pieces ? parseInt(it.quantity_in_pieces, 10) : 0,
            // price is optional (migration 061): send null (unpriced) when blank
            // rather than 0, so an unpriced line stays distinct from a ₹0 line.
            price: (it.price !== '' && it.price !== null && it.price !== undefined) ? parseFloat(it.price) : null,
            remark: it.remark || '',
            load_by_pieces: !!(it.load_by_pieces),
            // Cut-pieces is a coil-line-only cutting instruction, gated behind the
            // coil-tracking feature — sent only for coil-tracked lines, so ordinary
            // lines are untouched. The backend also drops it for non-coil lines.
            ...(isCoilTrackedLine(it) && it.cut_pieces ? { cut_pieces: parseInt(it.cut_pieces, 10) } : {}),
            // Only sent when coils were actually picked, so an ordinary order goes over
            // the wire exactly as it always has.
            ...(it.coil_picks?.length ? { coil_picks: it.coil_picks.map(p => ({ coil_id: Number(p.coil_id), take_kg: Number(p.take_kg), ...(p.remainder_kg === undefined || p.remainder_kg === null ? {} : { remainder_kg: Number(p.remainder_kg) }) })) } : {}),
          })),
        };
        // include source mapping if provided
        if (formOrder.source_type) {
          payload.source_type = formOrder.source_type;
          if (formOrder.source_type === 'warehouse') payload.warehouse_id = formOrder.selected_warehouse_id ? Number(formOrder.selected_warehouse_id) : null;
          if (formOrder.source_type === 'supplier') payload.supplier_id = formOrder.selected_supplier_id ? Number(formOrder.selected_supplier_id) : null;
        }
        const isNewOrder = !orderId && !currentOrder?.id && !order?.id;
        if (isNewOrder) {
          const result = await ordersService.createOrderForExistingCustomerWithAttachments(payload, pendingFiles);
          if (!result.success) throw new Error(result.error || 'Failed to submit order');
          if (onSuccess) onSuccess(result.data); else onClose();
        } else {
          // Edit path: close ONLY on success — a failed update must not discard the edits.
          const result = await onSubmit(payload);
          if (!result || result.success !== true) throw new Error((result && result.error) || 'Failed to submit order');
          onClose();
        }
      } else {
        const payload = {
          customer_id: 0,
          location_id: Number(formOrder.location_id) || 0,
          price_list_date: formOrder.price_list_date || null,
          expected_delivery_date: formOrder.expected_delivery_date || null,
          source_type: formOrder.source_type || 'warehouse',
          warehouse_id: formOrder.source_type === 'warehouse' && formOrder.selected_warehouse_id ? Number(formOrder.selected_warehouse_id) : 0,
          supplier_id: formOrder.source_type === 'supplier' && formOrder.selected_supplier_id ? Number(formOrder.selected_supplier_id) : 0,
          freight: formOrder.freight ? parseFloat(formOrder.freight) : 0,
          freight_type: formOrder.freight_type || 'freight_chargeable',
          freight_pricing_type: formOrder.freight_pricing_type || 'fixed',
          loading_cost_per_kg: formOrder.loading_cost_per_kg ? parseFloat(formOrder.loading_cost_per_kg) : 0,
          gst_included: !!formOrder.gst_applicable,
          gstn_number: newCustomer.gst_number || '',
          tcs_applicable: false,
          tcs_percentage: 0.1,
          sms_notification: false,
          reference_party_name: formOrder.reference_party_name || newCustomer.reference_party_name || '',
          remark: formOrder.remark || '',
          order_internal_remark: formOrder.order_internal_remark || '',
          payment_terms: formOrder.payment_terms ? String(formOrder.payment_terms) : null,
          owner_name: newCustomer.owner_name || '',
          company_name: newCustomer.company_name || '',
          gst_number: newCustomer.gst_number || '',
          contact_person: newCustomer.contact_person || '',
          mobile_number: newCustomer.phone_number_1 || '',
          phone_number: newCustomer.phone_number_2 || '',
          email: newCustomer.email || '',
          address_line1: newCustomer.address_line1 || '',
          address_line2: newCustomer.address_line2 || '',
          city: newCustomer.city || '',
          state: newCustomer.state || '',
          country: newCustomer.country || 'India',
          zip_code: newCustomer.zip_code || '',
          tally_name: newCustomer.tally_name || '',
          credit_period: Number(newCustomer.credit_period) || 0,
          warehouses: (newCustomerWarehouses || []).map(wh => ({
            name: wh.name || '',
            location_id: Number(wh.location_id) || 0,
            country: wh.country || 'India',
            state: wh.state || '',
            city: wh.city || '',
            address: wh.address || '',
            map_url: wh.map_url || '',
            contact_person: wh.contact_person || '',
            contact_phone: wh.contact_phone || ''
          })),
          items: (formOrder.items || []).filter(it => it.product_size_id).map(it => ({
            product_size_id: Number(it.product_size_id) || 0,
            unit: it.unit || '',
            length: it.length ? parseFloat(it.length) : 0,
            length_note: isCoilProductLine(it) ? (it.length_note || null) : null,
            quantity: (it.quantity !== '' && it.quantity !== null && it.quantity !== undefined) ? parseFloat(it.quantity) : 0,
            quantity_in_pieces: it.quantity_in_pieces ? parseInt(it.quantity_in_pieces, 10) : 0,
            // price is optional (migration 061): send null (unpriced) when blank.
            price: (it.price !== '' && it.price !== null && it.price !== undefined) ? parseFloat(it.price) : null,
            remark: it.remark || '',
            load_by_pieces: !!(it.load_by_pieces),
            // Cut-pieces is a coil-line-only cutting instruction, gated behind the
            // coil-tracking feature — sent only for coil-tracked lines, so ordinary
            // lines are untouched. The backend also drops it for non-coil lines.
            ...(isCoilTrackedLine(it) && it.cut_pieces ? { cut_pieces: parseInt(it.cut_pieces, 10) } : {}),
            // Only sent when coils were actually picked, so an ordinary order goes over
            // the wire exactly as it always has.
            ...(it.coil_picks?.length ? { coil_picks: it.coil_picks.map(p => ({ coil_id: Number(p.coil_id), take_kg: Number(p.take_kg), ...(p.remainder_kg === undefined || p.remainder_kg === null ? {} : { remainder_kg: Number(p.remainder_kg) }) })) } : {}),
          }))
        };
        const isNewOrderNC = !orderId && !currentOrder?.id && !order?.id;
        if (isNewOrderNC) {
          const result = await ordersService.createOrderForNewCustomerWithAttachments(payload, pendingFiles);
          if (!result.success) throw new Error(result.error || 'Failed to submit order');
          if (onSuccess) onSuccess(result.data); else onClose();
        } else {
          // Edit path: close ONLY on success — a failed update must not discard the edits.
          const result = await onSubmit(payload);
          if (!result || result.success !== true) throw new Error((result && result.error) || 'Failed to submit order');
          onClose();
        }
      }
    } catch (err) {
      const d = err?.response?.data;
      const message = d?.error?.message || d?.message || (typeof d?.error === 'string' ? d.error : null) || err?.message || 'Failed to submit order';
      setErrors(prev => ({ ...prev, submit: message }));
    } finally {
      setSubmitting(false);
    }
  };

  // Build augmented options that always include the current order's entities,
  // even if they are not in the initial 20 loaded from the API.
  const activeOrderData = currentOrder || order;
  const customerSelectOptions = (() => {
    const opts = customers.map(c => ({ value: c.id, label: c.tally_name || c.company_name || c.owner_name || c.name }));
    if (activeOrderData?.customer_id && (activeOrderData?.customer_tally_name || activeOrderData?.customer_name) && !opts.some(o => String(o.value) === String(activeOrderData.customer_id))) {
      opts.push({ value: activeOrderData.customer_id, label: activeOrderData.customer_tally_name || activeOrderData.customer_name });
    }
    return opts;
  })();

  const supplierSelectOptions = (() => {
    const opts = suppliers.map(s => ({ value: s.id, label: s.tally_name || s.company_name || s.owner_name || s.name }));
    if (activeOrderData?.supplier_id && (activeOrderData?.supplier_tally_name || activeOrderData?.supplier_name) && !opts.some(o => String(o.value) === String(activeOrderData.supplier_id))) {
      opts.push({ value: activeOrderData.supplier_id, label: activeOrderData.supplier_tally_name || activeOrderData.supplier_name });
    }
    return opts;
  })();

  // The customer's own address, offered as a dropdown option when it could actually
  // become a location row. In `new` mode it comes from the address just typed into the
  // form, since no customer record exists yet.
  const customerAddressDraft = useMemo(() => {
    if (mode === 'new') return buildCustomerAddressDraft(newCustomer);
    if (!formOrder.customer_id) return null;
    const listRow = customers.find(c => String(c.id) === String(formOrder.customer_id));
    // The list payload omits the address lines, so the detail fetch is merged over it.
    return buildCustomerAddressDraft({ ...(listRow || {}), ...(selectedCustomerDetail || {}) });
  }, [mode, formOrder.customer_id, customers, selectedCustomerDetail, newCustomer]);

  const deliveryLocationOptions = useMemo(() => {
    const isNew = mode === 'new';
    const customer = isNew
      ? null
      : customers.find(c => String(c.id) === String(formOrder.customer_id));
    return buildDeliveryLocationOptions({
      warehouses: isNew
        ? (Array.isArray(newCustomerWarehouses) ? newCustomerWarehouses : [])
        : (formOrder.customer_id && Array.isArray(customer?.warehouses) ? customer.warehouses : []),
      warehouseShape: isNew ? 'offline' : 'saved',
      locations,
      selectedId: formOrder.location_id,
      addressDraft: customerAddressDraft,
      labels: {
        warehouse: '(Warehouse)',
        default: '[Default]',
        useCustomerAddress: t('customerWarehouses.labels.useCustomerAddress'),
        needsDetails: t('customerWarehouses.labels.needsDetails'),
      },
    });
  }, [mode, formOrder.customer_id, formOrder.location_id, customers, locations, newCustomerWarehouses, customerAddressDraft, t]);

  // Picking the customer's address resolves it to a real location BEFORE it reaches
  // form state — `location_id` never holds the sentinel, so the submit path is untouched.
  const defaultSiteName = useCallback(() => {
    if (mode === 'new') return newCustomer?.company_name || newCustomer?.owner_name || '';
    const listRow = customers.find(c => String(c.id) === String(formOrder.customer_id));
    return listRow?.tally_name || listRow?.company_name || listRow?.owner_name || '';
  }, [mode, newCustomer, customers, formOrder.customer_id]);

  /** Save a completed draft as a delivery site and select it. */
  const commitCustomerAddress = useCallback(async (draft, siteName) => {
    const isNew = mode === 'new';
    const result = await resolveCustomerAddress({
      mode: isNew ? 'new' : 'existing',
      customerId: formOrder.customer_id,
      draft,
      warehouseName: siteName || defaultSiteName(),
    });

    if (!result.success) {
      setErrors(prev => ({ ...prev, location_id: result.error }));
      return;
    }

    // Reuse the existing add-location success path: it prepends to `locations`, merges
    // into window.ordersFormCache so a cleared search does not drop the new row, and
    // selects it.
    handleAddLocationSuccess(result.location);

    if (isNew) {
      setNewCustomerWarehouses(prev => [...prev, result.warehouse]);
    } else if (result.warehouse) {
      // Fold the new site into the customer in state so it re-renders as a saved
      // warehouse and the address option disappears.
      setCustomers(prev => prev.map(c => (
        String(c.id) === String(formOrder.customer_id)
          ? { ...c, warehouses: [...(c.warehouses || []),
              // Carry the nested location: the option-builder keys the
              // "already covered" check off it, so without it the address
              // option would linger after being saved.
              { ...result.warehouse, location: result.warehouse?.location || result.location }] }
          : c
      )));
    }
    setErrors(prev => ({ ...prev, location_id: undefined }));
    return result;
  }, [mode, formOrder.customer_id, resolveCustomerAddress, defaultSiteName, handleAddLocationSuccess]);

  /**
   * Picking "use customer's address". Complete addresses save straight through; an
   * incomplete one (no city, which is almost every imported customer) opens the
   * warehouse form prefilled so the missing part is typed once and then remembered.
   */
  const handleCustomerAddressPick = useCallback(() => {
    if (!customerAddressDraft?.offerable || resolving) return;
    if (customerAddressDraft.materializable) {
      commitCustomerAddress(customerAddressDraft, defaultSiteName());
    } else {
      setShowAddressPrompt(true);
    }
  }, [customerAddressDraft, resolving, commitCustomerAddress, defaultSiteName]);

  // Memoized: CustomerWarehouseForm re-seeds its fields whenever this prop's IDENTITY
  // changes, so a fresh literal each render would wipe what the user is typing the
  // moment anything re-renders the parent.
  const addressPromptInitialData = useMemo(() => ({
    name: defaultSiteName(),
    country: customerAddressDraft?.country || 'India',
    state: customerAddressDraft?.state || '',
    city: customerAddressDraft?.city || '',
    address: customerAddressDraft?.delivery_location || '',
  }), [defaultSiteName, customerAddressDraft]);

  const getProductForCalc = (id) => {
    const pid = String(id);
    if (!pid) return null;
    return (productSizes || []).find(p => String(p.id) === pid) || (cache.productSizes || []).find(p => String(p.id) === pid) || (currentOrder?.items || []).find(it => String(it.product_size_id) === pid);
  };

  const augmentedProductSizes = (() => {
    const base = [...(productSizes || [])];
    // Keep products from the loaded order (edit mode)
    (activeOrderData?.items || []).forEach(item => {
      const pid = String(item.product_size_id);
      if (pid && !base.some(p => String(p.id) === pid)) {
        // coil_tracked comes along for the ride: the row's coil strip is gated on the
        // product found here, so a coil-tracked size that is not in the first page of
        // the catalogue would otherwise lose its picker when the order is reopened.
        // A line that already carries allocations is coil-tracked by definition.
        base.push({ id: item.product_size_id, alias_name: item.size_name || item.product_name || `Product ${pid}`, product_weight: item.product_weight, standard_length: item.standard_length, coil_tracked: !!(item.coil_tracked || (item.coil_allocations || []).length) });
      }
    });
    // Keep products currently selected in the form (covers search-selected products for new orders)
    (formOrder.items || []).forEach(item => {
      const pid = String(item.product_size_id);
      if (pid && !base.some(p => String(p.id) === pid)) {
        const found = (productSizes || []).find(p => String(p.id) === pid);
        if (found) base.push(found);
      }
    });
    return base;
  })();

  /**
   * Coils picked for one line. Deliberately not routed through handleItemChange:
   * picking coils is not a quantity edit, so it must not re-derive pieces, re-stamp the
   * product name, or trip the auto-add-next-row heuristic — entering a line now and
   * picking its coils later is a normal way to work.
   */
  /**
   * Whether this line's product is sold coil by coil.
   *
   * `getProductForCalc` is deliberately part of the lookup: a coil-tracked size beyond
   * the first page of product sizes is only found there, and missing it would apply the
   * piece arithmetic to a roll.
   */
  const isCoilTrackedLine = useCallback((item) => {
    // BOTH toggles, exactly like `renderCoilPickStrip` below and `isCoilLine` on the
    // loading screen. Without the global half, switching the feature off left a line
    // whose SKU still carries the per-size flag with a read-only Pieces field and no
    // pieces arithmetic — but no coil picker to explain either, since that IS gated.
    // The operator would face a field they can neither fill nor understand.
    if (!coilTrackingEnabled || !item?.product_size_id) return false;
    const product = augmentedProductSizes.find(p => String(p.id) === String(item.product_size_id))
      || getProductForCalc(item.product_size_id);
    return Boolean(product?.coil_tracked);
  }, [coilTrackingEnabled, augmentedProductSizes, getProductForCalc]);

  const isCoilProductLine = useCallback((item) => {
    return isCoilTrackedLine(item);
  }, [isCoilTrackedLine]);

  /**
   * Kilograms the coil picks are measured against. Coil-tracked lines are entered and
   * billed in kg like everything else, but the unit box is free, so a metre or piece
   * line is converted the same way the row's own approx-weight readout converts it —
   * otherwise the picker would call a 500 m line "500 kg short".
   */
  const lineTargetKg = (it) => {
    const itUnit = (it.unit || '').toLowerCase();
    const qty = parseFloat(it.quantity) || 0;
    const len = parseFloat(it.length) || 0;
    // kg, or a pieces line (whose `quantity` carries the optional kg weight —
    // see app/utils/units.py: weight loops treat is_weight OR is_pieces as
    // "quantity is already kg"). Without the pieces arm a pcs line fell through
    // to the length formula below and the coil target came out several times the
    // real one, so a correct pick reported as massively short.
    if (isWeightUnit(itUnit) || isPieceUnit(itUnit)) return qty;

    const isOriginalProduct = it.original_product_size_id && String(it.product_size_id) === String(it.original_product_size_id);
    let pWeight = 0;
    let sLength = 0;
    if (isOriginalProduct && it.standard_weight && it.standard_length) {
      pWeight = parseFloat(it.standard_weight) || 0;
      sLength = parseFloat(it.standard_length) || 1;
    } else {
      const product = getProductForCalc(it.product_size_id);
      if (product) {
        pWeight = parseFloat(product.standard_weight || product.product_weight) || 0;
        sLength = parseFloat(product.standard_length) || 1;
      }
    }
    if (!(pWeight > 0 && sLength > 0)) return 0;

    if (isLengthUnit(itUnit)) return (pWeight / sLength) * qty;
    const effectiveLen = len > 0 ? len : sLength;
    return (effectiveLen * pWeight * qty) / sLength;
  };

  /**
   * The coil block for one order line, rendered identically by both item grids.
   *
   * Three outcomes, never two: the picker strip, a muted note saying why picking is not
   * possible on this order, or nothing at all. "Nothing" is reserved for the cases where
   * coils are genuinely not part of this line — the global setting is off, or the size is
   * not coil tracked — because the feature has to be invisible when it is off. A
   * supplier-direct order (or one with no warehouse chosen yet) DOES involve coils, it
   * just has no stock to pick from yet, and silently hiding the strip there reads as a
   * missing feature rather than a missing prerequisite.
   */
  const renderCoilPickStrip = (it, idx) => {
    if (!coilTrackingEnabled || !it.product_size_id) return null;

    // augmentedProductSizes already folds in the catalogue, the loaded order's products
    // and whatever the row has selected; getProductForCalc adds the cached page behind it.
    const product = augmentedProductSizes.find(p => String(p.id) === String(it.product_size_id)) || getProductForCalc(it.product_size_id);
    if (!product?.coil_tracked) return null;

    // Coils live in a warehouse, so a supplier-direct order has nothing to pick from.
    if (formOrder.source_type !== 'warehouse' || !formOrder.selected_warehouse_id) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
          <FontAwesomeIcon icon={faLayerGroup} />
          {t('coils.line.disabledNote')}
        </div>
      );
    }

    return (
      <div className="mt-2">
        <CoilPickStrip
          picks={it.coil_picks || []}
          targetKg={lineTargetKg(it)}
          warehouseId={formOrder.selected_warehouse_id}
          productSizeId={it.product_size_id}
          onChange={(picks) => handleCoilPicksChange(idx, picks)}
          context="order"
        />
      </div>
    );
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <style>{`.orders-form-scroll{ -ms-overflow-style: none; scrollbar-width: none; } .orders-form-scroll::-webkit-scrollbar{ display: none; }`}</style>
      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col relative" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        {submitting && (
          <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.OVERLAY, 0.6) }}>
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.6) }}></div>
          </div>
        )}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          { /* Determine edit mode early: treat presence of orderId, currentOrder or order as editing */}
          <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{(orderId || currentOrder || order) ? formStrings.title.edit : formStrings.title.add}</h2>
          <button onClick={onClose} className="p-2 rounded-lg" style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="overflow-y-auto orders-form-scroll flex-1">
          {loadOrderError ? (
            <div className="p-6">
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <p className="text-base font-semibold" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {t('ordersModule.common.loadFailed', 'Could not load this order. It may have been deleted, or the server is unavailable.')}
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-5 px-6 py-2.5 rounded-xl font-semibold"
                  style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text, border: `2px solid ${UI_COLORS.BUTTONS.SECONDARY.border}` }}
                >
                  {t('common.buttons.close', 'Close')}
                </button>
              </div>
            </div>
          ) : (loadingOrder || (isOpen && orderId && !currentOrder && !order)) ? (
            <div className="p-6">
              <div className="flex flex-col items-center justify-center py-20">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}></div>
                <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{labelText(strings.loading) || labelText(formStrings.loading) || t('ordersModule.common.loading')}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{formStrings.labels.customerMode}</label>
                {!(currentOrder || order) ? (
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2"><input type="radio" name="mode" checked={mode === 'existing'} onChange={() => setMode('existing')} /> {formStrings.customerModes.existing}</label>
                    <label className="flex items-center gap-2"><input type="radio" name="mode" checked={mode === 'new'} onChange={() => setMode('new')} /> {formStrings.customerModes.new}</label>
                  </div>
                ) : null}
              </div>

              {mode === 'existing' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{strings.fields.customerName} <span>*</span></label>
                      <SearchableSelect
                        name="customer_id"
                        value={formOrder.customer_id}
                        onChange={handleFormOrderChange}
                        options={customerSelectOptions}
                        placeholder={dropdownsLoading ? formStrings.loading.customers : strings.fields.customerName}
                        disabled={dropdownsLoading}
                        error={errors.customer_id}
                        onSearchChange={handleCustomerSearch}
                        loading={customerSearchLoading}
                        noOptionsMessage="Customer not found"
                      />
                      {errors.customer_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.customer_id}</p>}
                      {(() => {
                        if (!formOrder.customer_id) return null;
                        const listC = customers.find(x => String(x.id) === String(formOrder.customer_id));
                        // Prefer the full detail (has GST + address lines); fall back
                        // to the list object so city/state show while detail loads.
                        const detail = selectedCustomerDetail && String(selectedCustomerDetail.id) === String(formOrder.customer_id)
                          ? selectedCustomerDetail
                          : null;
                        const c = { ...(listC || {}), ...(detail || {}) };
                        if (!c || (!listC && !detail)) return null;
                        const gst = c.gst_number || c.gstn_number || c.gstNumber || '';
                        const addr = [
                          c.address_1 || c.address_line1 || c.address1,
                          c.address_2 || c.address_line2 || c.address2,
                          c.city,
                          c.state,
                          c.zip_code || c.zipCode || c.pincode,
                        ].filter(Boolean).join(', ');
                        if (!gst && !addr) return null;
                        return (
                          <div className="mt-1 text-xs leading-snug" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            {gst && <div><span className="font-medium">{t('commonCols.gstNo')}:</span> {gst}</div>}
                            {addr && <div><span className="font-medium">{t('commonCols.address')}:</span> {addr}</div>}
                          </div>
                        );
                      })()}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{strings.fields.deliveryLocation} <span>*</span></label>
                        <SearchableSelect
                          name="location_id"
                          value={formOrder.location_id}
                          onChange={handleFormOrderChange}
                          options={deliveryLocationOptions}
                          placeholder={dropdownsLoading ? formStrings.loading.locations : strings.fields.deliveryLocation}
                          // Disabled while resolving so a second click cannot create a
                          // duplicate delivery site for the same address.
                          disabled={dropdownsLoading || resolving}
                          error={errors.location_id}
                          onSearchChange={handleLocationSearch}
                          loading={locationSearchLoading || resolving}
                          onAddNew={(term) => { setLocationAddInitialValue(term); setShowAddLocationModal(true); }}
                        />
                    </div>
                    {errors.location_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.location_id}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightPricingType')}</label>
                      <SearchableSelect
                        options={[
                          { value: 'fixed', label: t('commonCols.statusOptions.fixed') },
                          { value: 'per_kg', label: t('commonCols.perKg') },
                        ]}
                        name="freight_pricing_type"
                        value={formOrder.freight_pricing_type}
                        onChange={handleFormOrderChange}
                        placeholder={t('common.formFields.placeholders.selectFreightPricingType')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightType')}</label>
                      <select name="freight_type" value={formOrder.freight_type} onChange={handleFormOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.freight_type ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}>
                        <option value="">{t('common.formFields.placeholders.selectFreightType')}</option>
                        <option value="ex_warehouse">{t('common.formFields.options.freightType.exWarehouse')}</option>
                        <option value="free_delivery_by_supplier">{t('common.formFields.options.freightType.freeDelivery')}</option>
                        <option value="freight_chargeable">{t('common.formFields.options.freightType.freightChargeable')}</option>
                        <option value="free_delivery_freight_to_pay">Free Delivery, Freight to Pay</option>
                        <option value="free_delivery_paid_by_company">Free Delivery, Paid by Company</option>
                      </select>
                      {errors.freight_type && (
                        <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}> {errors.freight_type} </p>
                      )}
                    </div>
                    <div>
                      {formOrder.freight_type !== 'free_delivery_by_supplier' && (
                        <>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            {strings.fields.freight}
                            {['ex_warehouse', 'free_delivery_paid_by_company'].includes(formOrder.freight_type) && <span className="ml-1 text-xs font-normal text-gray-400">(Reference Only)</span>}
                          </label>
                          <input name="freight" value={formOrder.freight} onChange={handleFormOrderChange} placeholder={labelText(strings.fields?.freight)} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.freight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.freight && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.freight}</p>}
                        </>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>Price List Date <span style={{ color: UI_COLORS.STATUS.ERROR }}>*</span></label>
                      <input type="date" name="price_list_date" value={formOrder.price_list_date} onChange={handleFormOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.price_list_date ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                      {errors.price_list_date && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.price_list_date}</p>}
                    </div>
                  </div>
                  {/* Expected delivery moved up out of the four-column row above, where it
                      was a fifth child wrapping onto a line of its own. Loading cost is a
                      two-or-three digit rate, so it sits in one column rather than half the
                      dialog, and its "per kg" note rides on the label instead of adding a
                      line under the field. */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{strings.fields.expectedDeliveryDate}</label>
                      <input type="date" name="expected_delivery_date" min={new Date().toLocaleDateString('en-CA')} value={formOrder.expected_delivery_date} onChange={handleFormOrderChange} placeholder={labelText(strings.fields?.expectedDeliveryDate)} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.expected_delivery_date ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                      {errors.expected_delivery_date && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.expected_delivery_date}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                        {labelText(formStrings.fields?.loadingCost)}
                        <span className="ml-1 text-xs font-normal text-gray-400">({labelText(formStrings.fields?.loadingCostNote)})</span>
                      </label>
                      <input name="loading_cost_per_kg" inputMode="decimal" value={formOrder.loading_cost_per_kg} onChange={handleFormOrderChange} placeholder="0.00" className="w-full px-4 py-3 rounded-xl border-2" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold">&nbsp;</label>
                      {(order?.id || currentOrder?.id) ? (
                        <AttachmentsPanel entityType="order" entityId={order?.id || currentOrder?.id} />
                      ) : (
                        <div className="rounded-xl border py-1.5 px-4 flex flex-col justify-center w-full" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                          <div className="flex items-center gap-4 w-full">
                            <label className="text-sm font-semibold whitespace-nowrap" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.attachments')}</label>
                            <div className="flex-1 flex items-center gap-2">
                              <input
                                ref={pendingFileInputRef}
                                type="file"
                                multiple
                                onChange={e => {
                                  const files = Array.from(e.target.files);
                                  setPendingFiles(prev => [...prev, ...files]);
                                }}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                              />
                              {pendingFiles.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setShowPendingList(!showPendingList)}
                                  className="px-2 py-1 rounded bg-gray-50 hover:bg-gray-100 border text-[11px] font-semibold flex items-center gap-1 transition-colors whitespace-nowrap"
                                  style={{ color: UI_COLORS.TEXT.PRIMARY, borderColor: UI_COLORS.BORDERS.LIGHT }}
                                >
                                  <FontAwesomeIcon icon={showPendingList ? faTimes : faEye} className="text-xs" />
                                  {showPendingList ? 'Close' : 'View'}
                                </button>
                              )}
                            </div>
                          </div>
                          {pendingFiles.length > 0 && !showPendingList && (
                            <p className="mt-1 text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{pendingFiles.length} file(s) selected — will be uploaded with the order</p>
                          )}
                          {showPendingList && pendingFiles.length > 0 && (
                            <div className="mt-2 border-t pt-2 w-full max-h-40 overflow-y-auto divide-y" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                              {pendingFiles.map((file, idx) => (
                                <div key={idx} className="flex items-center justify-between py-1 text-xs">
                                  <div className="flex items-center gap-2 truncate">
                                    <FontAwesomeIcon icon={faPaperclip} className="text-gray-400 text-[10px]" />
                                    <span className="font-medium truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{file.name}</span>
                                    <span className="text-[10px] text-gray-400">({formatBytes(file.size)})</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPendingFiles(prev => {
                                        const next = prev.filter((_, i) => i !== idx);
                                        if (next.length === 0 && pendingFileInputRef.current) {
                                          pendingFileInputRef.current.value = '';
                                        }
                                        return next;
                                      });
                                    }}
                                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{labelText(formStrings.fields?.sourceType)} <span>*</span></label>
                      <select name="source_type" value={formOrder.source_type} onChange={handleFormOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.source_type ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}>
                        <option value="">{labelText(formStrings.placeholders?.selectSource)}</option>
                        <option value="warehouse">{labelText(formStrings.fields?.sourceType?.options?.warehouse)}</option>
                        <option value="supplier">{labelText(formStrings.fields?.sourceType?.options?.supplier)}</option>
                      </select>
                      {errors.source_type && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.source_type}</p>}
                    </div>
                    <div>
                      {formOrder.source_type === 'warehouse' ? (
                        <>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{labelText(formStrings.fields?.warehouse)} <span>*</span></label>
                          <SearchableSelect
                            name="selected_warehouse_id"
                            value={formOrder.selected_warehouse_id}
                            onChange={handleFormOrderChange}
                            options={warehouses.map(w => ({
                              value: w.id,
                              label: `${w.name || w.alias_name || w.city} ${w.is_active ? '🟢' : '🔴'}`
                            }))}
                            placeholder={labelText(formStrings.placeholders?.selectWarehouse)}
                            error={errors.selected_warehouse_id}
                          />
                          {errors.selected_warehouse_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.selected_warehouse_id}</p>}
                        </>
                      ) : formOrder.source_type === 'supplier' ? (
                        <>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{labelText(formStrings.fields?.sourceType?.options?.supplier) || 'Supplier'} <span>*</span></label>
                          <SearchableSelect
                            name="selected_supplier_id"
                            value={formOrder.selected_supplier_id}
                            onChange={handleFormOrderChange}
                            options={supplierSelectOptions}
                            placeholder={labelText(formStrings.placeholders?.selectSupplier)}
                            error={errors.selected_supplier_id}
                            onSearchChange={handleSupplierSearch}
                            loading={supplierSearchLoading}
                          />
                          {errors.selected_supplier_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.selected_supplier_id}</p>}
                        </>
                      ) : (
                        <div className="pt-3 text-sm text-gray-500"></div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.referenceParty')}</label>
                      <input name="reference_party_name" value={formOrder.reference_party_name} onChange={handleFormOrderChange} placeholder={t('commonForm.referencePartyPlaceholder')} className="w-full px-4 py-3 rounded-xl border-2" />
                    </div>
                  </div>

                  {/* A single checkbox does not need a three-column grid row to itself. */}
                  <label className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                    <input type="checkbox" name="gst_applicable" checked={!!formOrder.gst_applicable} onChange={handleFormOrderChange} />
                    <span>{labelText(formStrings.fields?.gstApplicable)}</span>
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>Payment Terms <span style={{ color: UI_COLORS.STATUS.ERROR }}>*</span></label>
                      <SearchableSelect
                        name="payment_terms"
                        value={formOrder.payment_terms}
                        onChange={handleFormOrderChange}
                        options={PAYMENT_TERMS_OPTIONS}
                        placeholder={t('common.formFields.placeholders.selectPaymentTerms')}
                        allowFreeText={true}
                      />
                      {errors.payment_terms && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.payment_terms}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{strings.fields.remark}</label>
                      <textarea name="remark" value={formOrder.remark} onChange={handleFormOrderChange} placeholder={labelText(strings.fields?.remark)} className="w-full px-4 py-3 rounded-xl border-2 h-24" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.internalRemarks')}</label>
                      <textarea name="order_internal_remark" value={formOrder.order_internal_remark} onChange={handleFormOrderChange} placeholder={t('common.formFields.placeholders.internalRemarksNotVisibleCustomer')} className="w-full px-4 py-3 rounded-xl border-2 h-24" style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: '#f0fdf4' }} />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold">{strings.fields.items}</h4>
                    {/* Show no stock message if warehouse is selected but has no items */}
                    {formOrder.source_type === 'warehouse' && formOrder.selected_warehouse_id && (() => {
                      const selectedWarehouse = warehouses.find(w => String(w.id) === String(formOrder.selected_warehouse_id));
                      return selectedWarehouse && Array.isArray(selectedWarehouse.items) && selectedWarehouse.items.length === 0;
                    })() && (
                        <div className="p-4 rounded-xl border-2 text-center" style={{ borderColor: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1) }}>
                          <p className="text-sm font-semibold" style={{ color: UI_COLORS.STATUS.WARNING }}>{formStrings.messages?.noStock}</p>
                        </div>
                      )}
                    {formOrder.items.map((it, idx) => (
                      <div key={idx} className="rounded-xl border p-3">
                        <div className={`grid gap-3 items-end grid-cols-6 ${isCoilTrackedLine(it) ? 'md:grid-cols-[2fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr_auto]' : 'md:grid-cols-[2fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr_auto]'}`}>
                          <div className="col-span-5 md:col-span-1 order-1 md:order-none">
                            <label className="block text-xs">{t('ordersModule.productSizes.forms.fields.product_size.label')} <span>*</span></label>
                            <SearchableSelect
                              value={it.product_size_id}
                              onChange={(e) => handleItemChange(idx, 'product_size_id', e.target.value)}
                              options={(() => {
                                const results = augmentedProductSizes;
                                const currentId = String(it.product_size_id);
                                if (!currentId) return results.map(p => ({ value: p.id, label: p.size_name || p.alias_name || p.product_name || p.name }));
                                const isIdInResults = results.some(p => String(p.id) === currentId);
                                if (isIdInResults) return results.map(p => ({ value: p.id, label: p.size_name || p.alias_name || p.product_name || p.name }));
                                const currentItem = (cache.productSizes || []).find(p => String(p.id) === currentId) || (currentOrder?.items || []).find(i => String(i.product_size_id) === currentId);
                                const rowOptions = currentItem ? [currentItem, ...results] : results;
                                // product_size_id FIRST — see the note in PurchaseOrdersForm: an order
                                // item's `id` is the line id, not the product's.
                                return rowOptions.map(p => ({ value: p.product_size_id ?? p.id, label: p.size_name || p.alias_name || p.product_name || p.name }));
                              })()}
                              placeholder={formStrings.placeholders.selectProductSize}
                              error={errors[`items.${idx}.product_size_id`]}
                              onSearchChange={(term) => handleProductSearch(term)}
                              loading={productSearchLoading}
                              noOptionsMessage="Item not found"
                            />
                            {errors[`items.${idx}.product_size_id`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.product_size_id`]}</p>}
                          </div>
                          <div className="col-span-2 md:col-span-1 order-3 md:order-none">
                            <label className="text-xs">{labelText(formStrings.fields?.unit)}</label>
                            <select value={it.unit} onChange={(e) => handleItemChange(idx, 'unit', e.target.value)} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.unit`] ? UI_COLORS.STATUS.ERROR : undefined }}>
                              {Object.entries(t('ordersModule.common.units', { returnObjects: true })).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                              ))}
                            </select>
                            {errors[`items.${idx}.unit`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.unit`]}</p>}
                          </div>
                          <div className="col-span-2 md:col-span-1 order-5 md:order-none">
                            <label className="text-xs">{labelText(strings.fields?.item_qty)} <span>*</span></label>
                            <input value={it.quantity} onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)} placeholder={labelText(strings.fields?.item_qty)} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.quantity`] ? UI_COLORS.STATUS.ERROR : undefined }} />
                            {errors[`items.${idx}.quantity`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity`]}</p>}
                          </div>
                          <div className="col-span-3 md:col-span-1 order-6 md:order-none">
                            <label className="text-xs">{labelText(formStrings.fields?.pieces)}</label>
                            <input value={it.quantity_in_pieces} onChange={(e) => handleItemChange(idx, 'quantity_in_pieces', e.target.value)} placeholder={labelText(formStrings.fields?.pieces)} readOnly={isCoilTrackedLine(it)} title={isCoilTrackedLine(it) ? t('coils.line.piecesAreCoils') : undefined} className="w-full px-3 py-2 rounded border-2 read-only:opacity-60 read-only:cursor-not-allowed" />
                            {errors[`items.${idx}.quantity_in_pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity_in_pieces`]}</p>}
                          </div>
                          {isCoilTrackedLine(it) && (
                            <div className="col-span-3 md:col-span-1 order-6 md:order-none">
                              <label className="text-xs">{t('coils.line.cutPieces')}</label>
                              <input value={it.cut_pieces} onChange={(e) => handleItemChange(idx, 'cut_pieces', e.target.value)} placeholder={t('coils.line.cutPieces')} title={t('coils.line.cutPiecesHint')} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.cut_pieces`] ? UI_COLORS.STATUS.ERROR : undefined }} />
                              {errors[`items.${idx}.cut_pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.cut_pieces`]}</p>}
                            </div>
                          )}
                          <div className="col-span-3 md:col-span-1 order-7 md:order-none">
                            <label className="text-xs">{labelText(strings.fields?.item_price)}</label>
                            <input value={it.price} onChange={(e) => handleItemChange(idx, 'price', e.target.value)} placeholder={labelText(strings.fields?.item_price)} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.price`] ? UI_COLORS.STATUS.ERROR : undefined }} />
                            {errors[`items.${idx}.price`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.price`]}</p>}
                          </div>
                          <div className="col-span-2 md:col-span-1 order-4 md:order-none">
                            <label className="text-xs">{labelText(formStrings.fields?.length)}</label>
                            <input value={it.length} onChange={(e) => handleItemChange(idx, 'length', e.target.value)} placeholder={labelText(formStrings.fields?.length)} className="w-full px-3 py-2 rounded border-2" />
                            {errors[`items.${idx}.length`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.length`]}</p>}
                          </div>
                          <div className="col-span-1 md:col-span-1 order-2 md:order-none flex items-end justify-center pb-0.5">
                            <button type="button" onClick={() => handleRemoveItem(idx)} className="p-2 rounded bg-red-50 text-red-600 w-full flex justify-center md:w-auto"><FontAwesomeIcon icon={faTrash} /></button>
                          </div>
                        </div>
                        <div className="mt-1.5 flex gap-4 text-xs text-gray-500">
                          {(() => {
                            const itUnit = (it.unit || '').toLowerCase();
                            const qty = parseFloat(it.quantity) || 0;
                            const isOriginalProduct = it.original_product_size_id && String(it.product_size_id) === String(it.original_product_size_id);
                            let pWeight = 0;
                            let sLength = 0;
                            if (isOriginalProduct && it.standard_weight && it.standard_length) {
                              pWeight = parseFloat(it.standard_weight) || 0;
                              sLength = parseFloat(it.standard_length) || 1;
                            } else {
                              const product = getProductForCalc(it.product_size_id);
                              if (product) {
                                pWeight = parseFloat(product.standard_weight || product.product_weight) || 0;
                                sLength = parseFloat(product.standard_length) || 1;
                              }
                            }
                            const lCost = parseFloat(formOrder.loading_cost_per_kg) || 0;
                            if (pWeight > 0 && sLength > 0) {
                              let approxW = 0;
                              if (isWeightUnit(itUnit) || isPieceUnit(itUnit)) { approxW = qty; }
                              else if (isLengthUnit(itUnit) && sLength > 0) { approxW = (pWeight / sLength) * qty; }
                              const totalLC = approxW * lCost;
                              return (
                                <>
                                  <span>{formStrings.fields?.items?.approxWeight}: <strong className="text-gray-700">{approxW.toFixed(3)} {t('ordersModule.common.units.kg')}</strong></span>
                                  <span>{formStrings.fields?.items?.loadingCost}: <strong className="text-gray-700">{t('ordersModule.common.symbols.currency')} {totalLC.toFixed(2)}</strong></span>
                                  <span className="font-semibold text-gray-800">{formStrings.fields?.items?.amount}: {t('ordersModule.common.symbols.currency')} {(amountBasisQty(it) * (parseFloat(it.price) || 0)).toFixed(2)}</span>
                                </>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        {renderCoilPickStrip(it, idx)}
                        <div className="mt-2 flex items-center gap-4">
                          {isCoilProductLine(it) && (
                            <div className="flex-1">
                              <label className="text-xs">{t('ordersModule.common.lengthNote')}</label>
                              <input value={it.length_note || ''} onChange={(e) => handleItemChange(idx, 'length_note', e.target.value)} placeholder={t('ordersModule.common.lengthNotePlaceholder')} className="w-full px-3 py-2 rounded border-2" maxLength={500} />
                            </div>
                          )}
                          <div className="flex-1">
                            <label className="text-xs">{labelText(formStrings.fields?.itemRemark)}</label>
                            <input value={it.remark} onChange={(e) => handleItemChange(idx, 'remark', e.target.value)} placeholder={labelText(formStrings.fields?.itemRemark)} className="w-full px-3 py-2 rounded border-2" />
                          </div>
                          <label className="flex items-center gap-1.5 cursor-pointer mt-4 flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={!!(it.load_by_pieces)}
                              onChange={(e) => handleItemChange(idx, 'load_by_pieces', e.target.checked)}
                              className="h-4 w-4 rounded"
                              style={{ accentColor: UI_COLORS.BRAND.PRIMARY }}
                            />
                            <span className="text-xs font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('common.formFields.labels.loadByPieces')}</span>
                          </label>
                        </div>
                      </div>
                    ))}
                    <div>
                      <button type="button" onClick={addItem} className="px-4 py-2 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}><FontAwesomeIcon icon={faPlus} className="mr-2" />{formStrings.buttons.addItem}</button>
                    </div>
                  </div>
                </div>
              )}

              {mode === 'new' && (
                <div>
                  {step === 0 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{formStrings.headings.customerInformation}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.ownerName.label')} <span>*</span></label>
                          <input name="owner_name" value={newCustomer.owner_name} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.ownerName'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.owner_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.owner_name && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.owner_name}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.companyName.label')} <span>*</span></label>
                          <input name="company_name" value={newCustomer.company_name} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.companyName'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.company_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.company_name && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.company_name}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.tallyName.label')} <span>*</span></label>
                          <input name="tally_name" value={newCustomer.tally_name} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.tallyName'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.tally_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.tally_name && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.tally_name}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.referenceParty')}</label>
                          <input name="reference_party_name" value={newCustomer.reference_party_name} onChange={handleNewCustomerChange} placeholder={t('commonForm.referencePartyPlaceholder')} className="w-full px-4 py-3 rounded-xl border-2" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            {t('ordersModule.customers.forms.fields.gstNumber.label')} 
                            {(newCustomer.isAuthorized !== false && !newCustomer.sister_company) && <span> *</span>}
                          </label>
                          <div className="flex items-center gap-4 mt-1">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                name="isAuthorized"
                                checked={!!newCustomer.isAuthorized}
                                onChange={handleNewCustomerChange}
                                className="h-4 w-4 rounded"
                                style={{ accentColor: UI_COLORS.BRAND.PRIMARY }}
                              />
                              <span className="text-xs font-medium" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('common.formFields.labels.unlistedDealer')}</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                name="sister_company"
                                checked={!!newCustomer.sister_company}
                                onChange={handleNewCustomerChange}
                                className="h-4 w-4 rounded"
                                style={{ accentColor: UI_COLORS.BRAND.PRIMARY }}
                              />
                              <span className="text-xs font-medium" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{labelText(t('ordersModule.customers.forms.fields.sisterCompany'))}</span>
                            </label>
                          </div>
                          <input name="gst_number" value={newCustomer.gst_number} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.gstNumber'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.gst_number ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.gst_number && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.gst_number}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.contactPerson.label')} <span>*</span></label>
                          <input name="contact_person" value={newCustomer.contact_person} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.contactPerson'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.contact_person ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.contact_person && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.contact_person}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.phoneNumber1.label')} <span>*</span></label>
                          <input name="phone_number_1" value={newCustomer.phone_number_1} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.phoneNumber1'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.phone_number_1 ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.phone_number_1 && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.phone_number_1}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.email.label')} <span>*</span></label>
                          <input name="email" value={newCustomer.email} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.email'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.email ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.email && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.email}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.address1.label')} <span>*</span></label>
                          <textarea name="address_line1" value={newCustomer.address_line1} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.address1'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.address_line1 ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.address_line1 && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.address_line1}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.city.label')} <span>*</span></label>
                          <input name="city" value={newCustomer.city} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.city'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.city ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.city && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.city}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.state.label')} <span>*</span></label>
                          <SearchableSelect
                            name="state"
                            value={newCustomer.state}
                            onChange={(e) => handleNewCustomerChange({ target: { name: 'state', value: e.target.value } })}
                            options={(t('locations.states', { returnObjects: true }) || []).map(s => ({ value: s.label, label: s.label }))}
                            placeholder={labelText(t('ordersModule.customers.forms.fields.state'))}
                            error={errors.state}
                          />
                          {errors.state && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.state}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.country.label')} <span>*</span></label>
                          <input name="country" value={newCustomer.country} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.country'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.country ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.country && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.country}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.zipCode.label')} <span>*</span></label>
                          <input name="zip_code" value={newCustomer.zip_code} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.zipCode'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.zip_code ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.zip_code && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.zip_code}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.customers.forms.fields.creditPeriod.label')} <span>*</span></label>
                          <input name="credit_period" value={newCustomer.credit_period} onChange={handleNewCustomerChange} placeholder={labelText(t('ordersModule.customers.forms.fields.creditPeriod'))} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.credit_period ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.credit_period && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.credit_period}</p>}
                        </div>
                      </div>

                      <div className="flex justify-end gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                        <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{t('ordersModule.common.buttons.cancel')}</button>
                        <button type="button" onClick={handleNext} className="px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>{formStrings.buttons.next} <FontAwesomeIcon icon={faArrowRight} className="ml-2" /></button>
                      </div>
                    </div>
                  )}
                  {step === 1 && (
                    <>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('common.formFields.labels.warehouseInformation')}</h3>
                          <button
                            type="button"
                            onClick={() => setShowWarehouseModal(true)}
                            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-105"
                            style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1), color: UI_COLORS.TEXT.PRIMARY }}
                          >
                            <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Warehouse
                          </button>
                        </div>

                        <p className="text-sm mb-4" style={{ color: UI_COLORS.TEXT.MUTED }}>
                          You can add one or more warehouses for this new customer now, or skip this step.
                        </p>

                        <div className="space-y-3 min-h-[200px]">
                          {newCustomerWarehouses.length === 0 ? (
                            <div className="flex flex-col items-center justify-center p-8 rounded-2xl border-2 border-dashed" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                              <FontAwesomeIcon icon={faWarehouse} className="text-3xl mb-2" style={{ color: UI_COLORS.TEXT.MUTED }} />
                              <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.empty.noWarehousesYet')}</p>
                              <p className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.empty.warehousesOptional')}</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {newCustomerWarehouses.map((wh, idx) => (
                                <div key={idx} className="p-4 rounded-xl border relative group" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.SECONDARY }}>
                                  <button
                                    type="button"
                                      onClick={() => setNewCustomerWarehouses(prev => {
                                        const filtered = prev.filter((_, i) => i !== idx);
                                        if (filtered.length > 0 && !filtered.some(w => w.is_default)) {
                                          filtered[0].is_default = true;
                                        }
                                        return filtered;
                                      })}
                                    className="absolute top-2 right-2 p-1.5 rounded-lg text-red-500 hover:bg-red-50 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-all"
                                  >
                                    <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                  </button>
                                  <div className="flex items-start gap-3">
                                    <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-500">
                                      <FontAwesomeIcon icon={faWarehouse} className="text-sm" />
                                    </div>
                                    <div className="overflow-hidden">
                                      <div className="flex items-center gap-2 overflow-hidden">
                                        <h4 className="font-semibold text-sm truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{wh.name}</h4>
                                        {wh.is_default && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold uppercase tracking-wider flex-shrink-0">{t('common.formFields.labels.default')}</span>
                                        )}
                                      </div>
                                      <p className="text-xs mt-0.5 truncate" style={{ color: UI_COLORS.TEXT.MUTED }}>{wh.address || wh.city || 'No address provided'}</p>
                                      {!wh.is_default && (
                                        <button
                                          type="button"
                                          onClick={() => setNewCustomerWarehouses(prev => prev.map((w, i) => ({ ...w, is_default: i === idx })))}
                                          className="text-[10px] mt-1 text-blue-600 hover:underline font-semibold"
                                        >
                                          Set as Default
                                        </button>
                                      )}
                                      {wh.contact_person && (
                                        <p className="text-[10px] mt-1 font-medium" style={{ color: UI_COLORS.BRAND.PRIMARY }}>
                                          Contact: {wh.contact_person}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mt-8 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                          <button type="button" onClick={handleBack} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>
                            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> {formStrings.buttons.back || 'Back'}
                          </button>
                          <button type="button" onClick={handleNext} className="ml-auto px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                            {newCustomerWarehouses.length === 0 ? 'Skip & Next' : (formStrings.buttons.next || 'Next')} <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                          </button>
                        </div>
                      </>
                    )}

                  {step === 2 && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{formStrings.headings.orderDetails}</h3>
                      {(newCustomer.owner_name || newCustomer.company_name) && (
                        <div className="flex items-center gap-4 px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.06) }}>
                          {newCustomer.owner_name && (
                            <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>Customer: <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{newCustomer.owner_name}</span></span>
                          )}
                          {newCustomer.company_name && (
                            <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>Company: <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{newCustomer.company_name}</span></span>
                          )}
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{strings.fields.deliveryLocation} <span>*</span></label>
                          <SearchableSelect
                            name="location_id"
                            value={formOrder.location_id}
                            onChange={handleFormOrderChange}
                            options={deliveryLocationOptions}
                            placeholder={dropdownsLoading ? formStrings.loading.locations : strings.fields.deliveryLocation}
                            // Disabled while resolving so a second click cannot create a
                            // duplicate delivery site for the same address.
                            disabled={dropdownsLoading || resolving}
                            error={errors.location_id}
                            onSearchChange={handleLocationSearch}
                            loading={locationSearchLoading || resolving}
                            onAddNew={(term) => { setLocationAddInitialValue(term); setShowAddLocationModal(true); }}
                          />
                        </div>
                        {errors.location_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.location_id}</p>}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{labelText(formStrings.fields?.sourceType)} <span>*</span></label>
                          <select name="source_type" value={formOrder.source_type} onChange={handleFormOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.source_type ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}>
                            <option value="">{labelText(formStrings.placeholders?.selectSource)}</option>
                            <option value="warehouse">{labelText(formStrings.fields?.sourceType?.options?.warehouse)}</option>
                            <option value="supplier">{labelText(formStrings.fields?.sourceType?.options?.supplier)}</option>
                          </select>
                          {errors.source_type && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.source_type}</p>}
                        </div>
                        <div>
                          {formOrder.source_type === 'warehouse' ? (
                            <>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{labelText(formStrings.fields?.warehouse)} <span>*</span></label>
                              <SearchableSelect
                                name="selected_warehouse_id"
                                value={formOrder.selected_warehouse_id}
                                onChange={handleFormOrderChange}
                                options={warehouses.map(w => ({ value: w.id, label: `${w.name || w.alias_name || w.city} ${w.is_active ? '🟢' : '🔴'}` }))}
                                placeholder={labelText(formStrings.placeholders?.selectWarehouse)}
                                error={errors.selected_warehouse_id}
                                onSearchChange={handleWarehouseSearch}
                                loading={warehouseSearchLoading}
                              />
                              {errors.selected_warehouse_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.selected_warehouse_id}</p>}
                            </>
                          ) : formOrder.source_type === 'supplier' ? (
                            <>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{labelText(formStrings.fields?.sourceType?.options?.supplier) || 'Supplier'} <span>*</span></label>
                              <SearchableSelect
                                name="selected_supplier_id"
                                value={formOrder.selected_supplier_id}
                                onChange={handleFormOrderChange}
                                options={supplierSelectOptions}
                                placeholder={labelText(formStrings.placeholders?.selectSupplier)}
                                error={errors.selected_supplier_id}
                                onSearchChange={handleSupplierSearch}
                                loading={supplierSearchLoading}
                              />
                              {errors.selected_supplier_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.selected_supplier_id}</p>}
                            </>
                          ) : (
                            <div className="pt-3 text-sm text-gray-500"></div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.referenceParty')}</label>
                          <input name="reference_party_name" value={formOrder.reference_party_name} onChange={handleFormOrderChange} placeholder={t('commonForm.referencePartyPlaceholder')} className="w-full px-4 py-3 rounded-xl border-2" />
                        </div>
                      </div>

                      {/* Order-level fields for new-customer flow (same as existing-customer) */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightPricingType')}</label>
                          <SearchableSelect
                            options={[
                              { value: 'fixed', label: t('commonCols.statusOptions.fixed') },
                              { value: 'per_kg', label: t('commonCols.perKg') },
                            ]}
                            name="freight_pricing_type"
                            value={formOrder.freight_pricing_type}
                            onChange={handleFormOrderChange}
                            placeholder={t('common.formFields.placeholders.selectFreightPricingType')}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightType')}</label>
                          <select name="freight_type" value={formOrder.freight_type} onChange={handleFormOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.freight_type ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}>
                            <option value="">{t('common.formFields.placeholders.selectFreightType')}</option>
                            <option value="ex_warehouse">{t('common.formFields.options.freightType.exWarehouse')}</option>
                            <option value="free_delivery_by_supplier">{t('common.formFields.options.freightType.freeDelivery')}</option>
                            <option value="freight_chargeable">{t('common.formFields.options.freightType.freightChargeable')}</option>
                            <option value="free_delivery_freight_to_pay">Free Delivery, Freight to Pay</option>
                            <option value="free_delivery_paid_by_company">Free Delivery, Paid by Company</option>
                          </select>
                          {errors.freight_type && (
                            <p
                              className="text-xs mt-1"
                              style={{ color: UI_COLORS.STATUS.ERROR }}
                            >
                              {errors.freight_type}
                            </p>
                          )}
                        </div>
                        <div>
                          {formOrder.freight_type !== 'free_delivery_by_supplier' && (
                            <>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                {strings.fields.freight}
                                {['ex_warehouse', 'free_delivery_paid_by_company'].includes(formOrder.freight_type) && <span className="ml-1 text-xs font-normal text-gray-400">(Reference Only)</span>}
                              </label>
                              <input name="freight" value={formOrder.freight} onChange={handleFormOrderChange} placeholder={labelText(strings.fields?.freight)} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.freight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.freight && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.freight}</p>}
                            </>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>Price List Date <span style={{ color: UI_COLORS.STATUS.ERROR }}>*</span></label>
                          <input type="date" name="price_list_date" value={formOrder.price_list_date} onChange={handleFormOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.price_list_date ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.price_list_date && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.price_list_date}</p>}
                        </div>
                      </div>
                      {/* Same regrouping as the existing-customer branch above. */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{strings.fields.expectedDeliveryDate}</label>
                          <input type="date" name="expected_delivery_date" min={new Date().toLocaleDateString('en-CA')} value={formOrder.expected_delivery_date} onChange={handleFormOrderChange} className="w-full px-4 py-3 rounded-xl border-2" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            {labelText(formStrings.fields?.loadingCost)}
                            <span className="ml-1 text-xs font-normal text-gray-400">({labelText(formStrings.fields?.loadingCostNote)})</span>
                          </label>
                          <input name="loading_cost_per_kg" inputMode="decimal" value={formOrder.loading_cost_per_kg} onChange={handleFormOrderChange} placeholder="0.00" className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.loading_cost_per_kg ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.loading_cost_per_kg && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.loading_cost_per_kg}</p>}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold">&nbsp;</label>
                          {(order?.id || currentOrder?.id) ? (
                            <AttachmentsPanel entityType="order" entityId={order?.id || currentOrder?.id} />
                          ) : (
                            <div className="rounded-xl border border-dashed px-4 py-2 text-sm text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, color: UI_COLORS.TEXT.MUTED }}>
                              Save the order first to attach files
                            </div>
                          )}
                        </div>
                      </div>

                      {/* A single checkbox does not need a three-column grid row to itself. */}
                      <label className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                        <input type="checkbox" name="gst_applicable" checked={!!formOrder.gst_applicable} onChange={handleFormOrderChange} />
                        <span>{labelText(formStrings.fields?.gstApplicable)}</span>
                      </label>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>Payment Terms <span style={{ color: UI_COLORS.STATUS.ERROR }}>*</span></label>
                          <SearchableSelect
                            name="payment_terms"
                            value={formOrder.payment_terms}
                            onChange={handleFormOrderChange}
                            options={PAYMENT_TERMS_OPTIONS}
                            placeholder={t('common.formFields.placeholders.selectPaymentTerms')}
                            allowFreeText={true}
                          />
                          {errors.payment_terms && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.payment_terms}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{strings.fields.remark}</label>
                          <textarea name="remark" value={formOrder.remark} onChange={handleFormOrderChange} placeholder={labelText(strings.fields?.remark)} className="w-full px-4 py-3 rounded-xl border-2 h-24" style={{ borderColor: errors.remark ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.remark && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.remark}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.internalRemarks')}</label>
                          <textarea name="order_internal_remark" value={formOrder.order_internal_remark} onChange={handleFormOrderChange} placeholder={t('common.formFields.placeholders.internalRemarksNotVisibleCustomer')} className="w-full px-4 py-3 rounded-xl border-2 h-24" style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: '#f0fdf4' }} />
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold">{strings.fields.items}</h4>
                        {/* Show no stock message if warehouse is selected but has no items */}
                        {formOrder.source_type === 'warehouse' && formOrder.selected_warehouse_id && (() => {
                          const selectedWarehouse = warehouses.find(w => String(w.id) === String(formOrder.selected_warehouse_id));
                          return selectedWarehouse && Array.isArray(selectedWarehouse.items) && selectedWarehouse.items.length === 0;
                        })() && (
                            <div className="p-4 rounded-xl border-2 text-center" style={{ borderColor: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1) }}>
                              <p className="text-sm font-semibold" style={{ color: UI_COLORS.STATUS.WARNING }}>{formStrings.messages?.noStock}</p>
                            </div>
                          )}
                        {formOrder.items.map((it, idx) => (
                          <div key={idx} className="rounded-xl border p-3">
                            <div className={`grid gap-3 items-end grid-cols-6 ${isCoilTrackedLine(it) ? 'md:grid-cols-[2fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr_auto]' : 'md:grid-cols-[2fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr_auto]'}`}>
                              <div className="col-span-5 md:col-span-1 order-1 md:order-none">
                                <label className="block text-xs">{t('ordersModule.productSizes.forms.fields.product_size.label')}</label>
                                <SearchableSelect
                                  value={it.product_size_id}
                                  onChange={(e) => handleItemChange(idx, 'product_size_id', e.target.value)}
                                  options={augmentedProductSizes.map(p => ({ value: p.id, label: p.size_name || p.alias_name || p.product_name || p.name }))}
                                  placeholder={formStrings.placeholders.selectProductSize}
                                  error={errors[`items.${idx}.product_size_id`]}
                                  onSearchChange={handleProductSearch}
                                  loading={productSearchLoading}
                                  noOptionsMessage="Item not found"
                                />
                                {errors[`items.${idx}.product_size_id`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.product_size_id`]}</p>}
                              </div>
                              <div className="col-span-2 md:col-span-1 order-3 md:order-none">
                                <label className="text-xs">{labelText(formStrings.fields?.unit)} <span>*</span></label>
                                <select value={it.unit} onChange={(e) => handleItemChange(idx, 'unit', e.target.value)} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.unit`] ? UI_COLORS.STATUS.ERROR : undefined }}>
                                  {Object.entries(t('ordersModule.common.units', { returnObjects: true })).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                  ))}
                                </select>
                                {errors[`items.${idx}.unit`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.unit`]}</p>}
                              </div>
                              <div className="col-span-2 md:col-span-1 order-5 md:order-none">
                                <label className="text-xs">{labelText(strings.fields?.item_qty)}</label>
                                <input value={it.quantity} onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)} placeholder={labelText(strings.fields?.item_qty)} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.quantity`] ? UI_COLORS.STATUS.ERROR : undefined }} />
                                {errors[`items.${idx}.quantity`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity`]}</p>}
                              </div>
                              <div className="col-span-3 md:col-span-1 order-6 md:order-none">
                                <label className="text-xs">{t('common.formFields.labels.pieces')}</label>
                                <input value={it.quantity_in_pieces} onChange={(e) => handleItemChange(idx, 'quantity_in_pieces', e.target.value)} placeholder="0" readOnly={isCoilTrackedLine(it)} title={isCoilTrackedLine(it) ? t('coils.line.piecesAreCoils') : undefined} className="w-full px-3 py-2 rounded border-2 read-only:opacity-60 read-only:cursor-not-allowed" style={{ borderColor: errors[`items.${idx}.quantity_in_pieces`] ? UI_COLORS.STATUS.ERROR : undefined }} />
                                {errors[`items.${idx}.quantity_in_pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity_in_pieces`]}</p>}
                              </div>
                              {isCoilTrackedLine(it) && (
                                <div className="col-span-3 md:col-span-1 order-6 md:order-none">
                                  <label className="text-xs">{t('coils.line.cutPieces')}</label>
                                  <input value={it.cut_pieces} onChange={(e) => handleItemChange(idx, 'cut_pieces', e.target.value)} placeholder={t('coils.line.cutPieces')} title={t('coils.line.cutPiecesHint')} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.cut_pieces`] ? UI_COLORS.STATUS.ERROR : undefined }} />
                                  {errors[`items.${idx}.cut_pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.cut_pieces`]}</p>}
                                </div>
                              )}
                              <div className="col-span-3 md:col-span-1 order-7 md:order-none">
                                <label className="text-xs">{labelText(strings.fields?.item_price)}</label>
                                <input value={it.price} onChange={(e) => handleItemChange(idx, 'price', e.target.value)} placeholder={labelText(strings.fields?.item_price)} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.price`] ? UI_COLORS.STATUS.ERROR : undefined }} />
                                {errors[`items.${idx}.price`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.price`]}</p>}
                              </div>
                              <div className="col-span-2 md:col-span-1 order-4 md:order-none">
                                <label className="text-xs">{labelText(formStrings.fields?.length)} <span>*</span></label>
                                <input value={it.length} onChange={(e) => handleItemChange(idx, 'length', e.target.value)} placeholder={labelText(formStrings.fields?.length)} className="w-full px-3 py-2 rounded border-2" />
                                {errors[`items.${idx}.length`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.length`]}</p>}
                              </div>
                              <div className="col-span-1 md:col-span-1 order-2 md:order-none flex items-end justify-center pb-0.5">
                                <button type="button" onClick={() => handleRemoveItem(idx)} className="p-2 rounded bg-red-50 text-red-600 w-full flex justify-center md:w-auto"><FontAwesomeIcon icon={faTrash} /></button>
                              </div>
                            </div>
                            <div className="mt-1.5 flex gap-4 text-xs text-gray-500">
                              {(() => {
                                const itUnit = (it.unit || '').toLowerCase();
                                const qty = parseFloat(it.quantity) || 0;
                                const isOriginalProduct = it.original_product_size_id && String(it.product_size_id) === String(it.original_product_size_id);
                                let pWeight = 0;
                                let sLength = 0;
                                if (isOriginalProduct && it.standard_weight && it.standard_length) {
                                  pWeight = parseFloat(it.standard_weight) || 0;
                                  sLength = parseFloat(it.standard_length) || 1;
                                } else {
                                  const product = getProductForCalc(it.product_size_id);
                                  if (product) {
                                    pWeight = parseFloat(product.standard_weight || product.product_weight) || 0;
                                    sLength = parseFloat(product.standard_length) || 1;
                                  }
                                }
                                const lCost = parseFloat(formOrder.loading_cost_per_kg) || 0;
                                if (pWeight > 0 && sLength > 0) {
                                  let approxW = 0;
                                  if (isWeightUnit(itUnit) || isPieceUnit(itUnit)) { approxW = qty; }
                                  else if (isLengthUnit(itUnit) && sLength > 0) { approxW = (pWeight / sLength) * qty; }
                                  const totalLC = approxW * lCost;
                                  return (
                                    <>
                                      <span>{formStrings.fields?.items?.approxWeight}: <strong className="text-gray-700">{approxW.toFixed(3)} {t('ordersModule.common.units.kg')}</strong></span>
                                      <span>{formStrings.fields?.items?.loadingCost}: <strong className="text-gray-700">{formatCurrency(totalLC)}</strong></span>
                                      <span className="font-semibold text-gray-800">{formStrings.fields?.items?.amount}: {formatCurrency(amountBasisQty(it) * (parseFloat(it.price) || 0))}</span>
                                    </>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            {renderCoilPickStrip(it, idx)}
                            <div className="mt-2 flex items-center gap-4">
                              {isCoilProductLine(it) && (
                                <div className="flex-1">
                                  <label className="text-xs font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('ordersModule.common.lengthNote')}</label>
                                  <input value={it.length_note || ''} onChange={(e) => handleItemChange(idx, 'length_note', e.target.value)} placeholder={t('ordersModule.common.lengthNotePlaceholder')} className="w-full px-3 py-2 rounded border-2" maxLength={500} />
                                </div>
                              )}
                              <div className="flex-1">
                                <label className="text-xs font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{labelText(formStrings.fields?.itemRemark)}</label>
                                <input
                                  value={it.remark}
                                  onChange={(e) => handleItemChange(idx, 'remark', e.target.value)}
                                  placeholder={labelText(formStrings.fields?.itemRemark)}
                                  className="w-full px-3 py-2 rounded-lg border-2 transition-all duration-200 focus:shadow-md"
                                  style={{
                                    backgroundColor: '#fef9c3',
                                    borderColor: it.remark ? '#fde047' : UI_COLORS.FORMS.INPUT.border,
                                    color: UI_COLORS.TEXT.PRIMARY
                                  }}
                                />
                              </div>
                              <label className="flex items-center gap-1.5 cursor-pointer mt-4 flex-shrink-0">
                                <input
                                  type="checkbox"
                                  checked={!!(it.load_by_pieces)}
                                  onChange={(e) => handleItemChange(idx, 'load_by_pieces', e.target.checked)}
                                  className="h-4 w-4 rounded"
                                  style={{ accentColor: UI_COLORS.BRAND.PRIMARY }}
                                />
                                <span className="text-xs font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('common.formFields.labels.loadByPieces')}</span>
                              </label>
                            </div>
                          </div>
                        ))}
                        <div>
                          <button type="button" onClick={addItem} className="px-4 py-2 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}><FontAwesomeIcon icon={faPlus} className="mr-2" />{formStrings.buttons.addItem}</button>
                        </div>
                      </div>

                      {errors.submit && (
                        <div className="mt-3 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>{errors.submit}</div>
                      )}
                      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 mt-6 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                        <div className="flex flex-col items-start md:items-end text-left md:text-right border-b pb-4 md:border-b-0 md:pb-0">
                          <div className="text-lg font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {labelText(formStrings.fields?.totalAmount)}: {t('ordersModule.common.symbols.currency')} {totalOrderAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          {totalOrderWeight > 0 && (
                            <div className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                              Total Weight: {totalOrderWeight.toFixed(3)} kg
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3 w-full md:w-auto">
                          <button type="button" onClick={handleBack} className="px-5 py-3 rounded-xl font-semibold flex-1 md:flex-initial text-center border" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}><FontAwesomeIcon icon={faArrowLeft} className="mr-2" />{formStrings.buttons.back}</button>
                          <button type="button" onClick={onClose} className="px-5 py-3 rounded-xl font-semibold flex-1 md:flex-initial text-center border" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{t('ordersModule.common.buttons.cancel')}</button>
                          <button type="submit" disabled={loading || submitting} className="px-5 py-3 rounded-xl font-semibold flex-1 md:flex-initial text-center" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                            <span className="inline-flex items-center justify-center w-full">
                              <span className={`${submitting ? 'animate-spin' : ''} mr-2`} style={{ display: submitting ? 'inline-block' : 'none' }}>
                                <div className="h-4 w-4 rounded-full border-2 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.text, 0.6) }}></div>
                              </span>
                              <FontAwesomeIcon icon={faSave} className="mr-2" />{formStrings.buttons.save}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>

                  )}
                </div>
              )}


              {mode === 'existing' && (
                <div className="mt-4">
                  {errors.submit && (
                    <div className="mb-3 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>{errors.submit}</div>
                  )}
                  <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <div className="text-lg font-bold border-b pb-4 md:border-b-0 md:pb-0" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                      {labelText(formStrings.fields?.totalAmount)}: {formatCurrency(totalOrderAmount)}
                    </div>
                    <div className="flex items-center gap-3 w-full md:w-auto">
                      <button type="button" onClick={onClose} className="px-5 py-3 rounded-xl font-semibold flex-1 md:flex-initial text-center border" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{t('ordersModule.common.buttons.cancel')}</button>
                      <button type="submit" disabled={loading || submitting} className="px-5 py-3 rounded-xl font-semibold flex-1 md:flex-initial text-center" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                        <span className="inline-flex items-center justify-center w-full">
                          {submitting && <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.text, 0.6) }}></div>}
                          <FontAwesomeIcon icon={faSave} className="mr-2" />{formStrings.buttons.save}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </form>
          )}

        </div>
        <AddLocationModal
          isOpen={showAddLocationModal}
          onClose={() => { setShowAddLocationModal(false); setLocationAddInitialValue(''); }}
          onSuccess={handleAddLocationSuccess}
          initialValues={{ delivery_location: locationAddInitialValue }}
        />

        {/* Completes the customer's address before it becomes a delivery site. Prefilled
            from their record; the missing part (almost always city) is typed once and is
            then remembered as a saved site. Offline so nothing is written until Save. */}
        <CustomerWarehouseForm
          isOpen={showAddressPrompt}
          onClose={() => setShowAddressPrompt(false)}
          isOffline={true}
          initialData={addressPromptInitialData}
          onSave={async (data) => {
            setShowAddressPrompt(false);
            // The modal also offers "use an existing location". Picking that returns a
            // location_id with the address fields left blank, so there is nothing to
            // resolve — just select it.
            if (data.location_id) {
              const picked = locations.find(l => String(l.id) === String(data.location_id));
              handleAddLocationSuccess(picked || { id: data.location_id });
              return;
            }
            await commitCustomerAddress(
              {
                materializable: true,
                delivery_location: data.address,
                city: data.city,
                state: data.state,
                country: data.country || 'India',
              },
              data.name
            );
          }}
        />

        <CustomerWarehouseForm
          isOpen={showWarehouseModal}
          onClose={() => setShowWarehouseModal(false)}
          isOffline={true}
          onSave={async (data) => {
            let locationId = data.location_id;

            // If it's a new location (not selected from existing), create it via API
            if (!locationId) {
              try {
                const locRes = await locationsService.createLocation({
                  delivery_location: (data.address && data.address.trim().length >= 5) 
                    ? data.address.trim() 
                    : `${data.name} - ${data.address || data.city || ''}`.trim(),
                  city: data.city,
                  state: data.state,
                  country: data.country || 'India',
                  map_url: data.map_url,
                  is_active: true
                });

                if (locRes.success && locRes.data?.id) {
                  locationId = locRes.data.id;
                }
              } catch (err) {
                console.error("Failed to create location for warehouse:", err);
              }
            }

            const updatedWarehouse = { ...data, location_id: locationId };
            setNewCustomerWarehouses(prev => {
              const isFirst = prev.length === 0;
              const shouldBeDefault = isFirst || updatedWarehouse.is_default;
              const newWh = { ...updatedWarehouse, is_default: shouldBeDefault };
              if (shouldBeDefault) {
                return [...prev.map(w => ({ ...w, is_default: false })), newWh];
              }
              return [...prev, newWh];
            });

            // Automatically use this location as the delivery location for the order
            if (locationId) {
              setFormOrder(prev => ({ ...prev, location_id: locationId }));
            }
          }}
        />
      </div>
    </div >
  );
};

export default OrdersForm;
